(function () {
  const STORAGE_KEYS = {
    quiz: 'plennia:quiz',
    pets: 'plennia:pets',
    activePet: 'plennia:active-pet',
    pendingPetProfile: 'plennia:pending-pet-profile',
    quizContext: 'plennia:quiz-context',
  };

  const PRODUCT_FETCH_LIMIT = 250;
  const DEFAULT_SUBSCRIPTION_DISCOUNT = 10; // % off for subscribers — update when selling plans are live
  const productCollectionCache = new Map();
  const customerPetsHydrationCache = new Map();
  // Keep the client/server contract explicit so the app-proxy endpoint and theme stay in sync.
  const CUSTOMER_PETS_SYNC_VERSION = '2026-04-05';
  const CUSTOMER_PETS_STORAGE_DEFAULTS = {
    namespace: 'custom',
    petsKey: 'pet_profiles',
    activePetKey: 'active_pet_id',
  };

  const PLAN_CONFIG = {
    tags: {
      species: {
        dog: ['species_dog', 'pet_dog'],
        cat: ['species_cat', 'pet_cat'],
      },
      age: {
        puppy: ['age_puppy', 'life_puppy'],
        kitten: ['age_kitten', 'life_kitten'],
        adult: ['age_adult', 'life_adult'],
        senior: ['age_senior', 'life_senior'],
      },
      formats: {
        dry: ['format_dry'],
        wet: ['format_wet'],
        treat: ['format_treat'],
      },
      roles: {
        main: ['role_main_food'],
        support: ['role_support_food'],
        addon: ['role_functional_treat'],
      },
      benefits: {
        skin_coat: ['benefit_skin_coat', 'goal_skin_coat', 'support_skin_coat'],
        joint: ['benefit_joint', 'goal_joint', 'goal_joints', 'support_joint_health'],
        dental: ['benefit_dental', 'goal_dental', 'support_dental', 'dental_for_dogs'],
        weight: ['benefit_weight', 'goal_weight_loss', 'goal_weight', 'support_weight_management'],
        digestive: ['benefit_digestive', 'goal_digestive', 'support_digestive', 'support_sensitive_digestion'],
        urinary: ['benefit_urinary', 'goal_urinary', 'support_urinary'],
        hairball: ['goal_hairball', 'benefit_hairball', 'support_hairball'],
      },
      proteins: {
        chicken: ['protein_chicken'],
        fish: ['protein_fish'],
        beef: ['protein_beef'],
        turkey: ['protein_turkey'],
        lamb: ['protein_lamb'],
        duck: ['protein_duck'],
        pork: ['protein_pork'],
      },
      priority: {
        high: ['quiz_priority_high'],
        medium: ['quiz_priority_medium'],
        hero: ['hero_product'],
        bestseller: ['bestseller'],
      },
    },
    labels: {
      primary_goal: {
        none: 'General wellbeing',
        skin_coat: 'Skin and coat',
        joint: 'Joint support',
        joints: 'Joint support',
        dental: 'Dental support',
        weight: 'Weight management',
        weight_loss: 'Weight management',
        digestive: 'Digestive support',
        digestion: 'Digestive support',
        urinary: 'Urinary support',
        hairball: 'Hairball support',
      },
      health_issues: {
        none: 'No major issues',
        skin_hair: 'Skin and hair',
        joint_problems: 'Joint problems',
        dental: 'Dental / oral problems',
        sensitive_digestion: 'Sensitive digestion',
        obesity: 'Obesity',
        shedding: 'Shedding',
        urinary: 'Urinary',
        allergies: 'Allergies',
      },
      allergies: {
        none: 'No ingredient allergies',
        chicken: 'Chicken',
        fish: 'Fish',
        beef: 'Beef',
        turkey: 'Turkey',
        lamb: 'Lamb',
        duck: 'Duck',
        pork: 'Pork',
      },
      body_condition: {
        underweight: 'Underweight',
        ideal: 'Ideal',
        overweight: 'Overweight',
      },
      activity_level: {
        low: 'Low activity',
        medium: 'Medium activity',
        high: 'High activity',
      },
      food_types: {
        dry: 'Dry food',
        wet: 'Wet food',
        treats: 'Treats',
      },
      age_group: {
        puppy: 'Puppy',
        kitten: 'Kitten',
        adult: 'Adult',
        senior: 'Senior',
      },
    },
    goalBenefits: {
      none: [],
      skin_coat: ['skin_coat'],
      joint: ['joint'],
      joints: ['joint'],
      dental: ['dental'],
      weight: ['weight'],
      weight_loss: ['weight'],
      digestive: ['digestive'],
      digestion: ['digestive'],
      urinary: ['urinary'],
      hairball: ['hairball'],
    },
    healthBenefits: {
      none: [],
      skin_hair: ['skin_coat'],
      joint_problems: ['joint'],
      joints: ['joint'],
      dental: ['dental'],
      sensitive_digestion: ['digestive'],
      digestion: ['digestive'],
      obesity: ['weight'],
      shedding: ['skin_coat'],
      urinary: ['urinary'],
      allergies: ['digestive'],
      hairball: ['hairball'],
    },
    scoring: {
      species: 120,
      age: 48,
      primaryBenefit: 56,
      secondaryBenefit: 32,
      currentFormat: 96,
      roleMain: 14,
      roleSupport: 10,
      roleAddon: 12,
      dryWeightBoost: 35,
      wetWeightPenalty: 18,
      seniorJointBoost: 22,
      seniorJointPriority: 44,
      skinMainPriority: 65,
      skinTreatPriority: 42,
      dentalDogTreatPriority: 85,
      dentalDogFoodPriority: 38,
      priorityHigh: 18,
      priorityMedium: 10,
      hero: 8,
      bestseller: 4,
    },
  };

  const PLAN_ACTIVITY_STEPS = [
    { score: 1, level: 'low', label: 'Low', description: 'Mostly restful days and short walks.' },
    { score: 2, level: 'low', label: 'Low', description: 'Light movement and gentle play.' },
    { score: 3, level: 'medium', label: 'Medium', description: 'Daily walks over 2 hours.' },
    { score: 4, level: 'high', label: 'High', description: 'Regular training, play, and energetic walks.' },
    { score: 5, level: 'high', label: 'High', description: 'Very active days with intense movement.' },
  ];

  const PLAN_SLOT_META = {
    primary: {
      label: 'Primary recommendation',
      heading: 'Best daily-fit product',
      body: 'The strongest match for daily feeding based on your pet profile and selected needs.',
    },
    secondary: {
      label: 'Secondary recommendation',
      heading: 'Best companion option',
      body: 'A complementary second product if you want another meal format or backup main choice.',
    },
    addon: {
      label: 'Functional treat',
      heading: 'Supportive treat',
      body: 'A treat pick that reinforces your pet\'s main goal when available in the catalog.',
    },
    booster: {
      label: 'Plennia Boost',
      heading: 'Daily functional booster',
      body: 'A targeted topper that reinforces your pet\'s primary goal. Sprinkle on dry food daily — coming soon.',
      comingSoon: true,
    },
  };

  const PLAN_GROUP_META = {
    dry: {
      eyebrow: 'Matched from Shopify tags',
      heading: 'Recommended Dry Food',
      empty: 'No dry food is tagged for this pet yet.',
    },
    wet: {
      eyebrow: 'Matched from Shopify tags',
      heading: 'Recommended Wet Food',
      empty: 'No wet food is tagged for this pet yet.',
    },
    treat: {
      eyebrow: 'Matched from Shopify tags',
      heading: 'Recommended Treats',
      empty: 'No treats are tagged for this pet yet.',
    },
  };

  const PLAN_FUTURE_SLOT_META = [
    {
      key: 'promotions',
      heading: 'Promotions',
      body: 'Reserved for future promotional placements tied to the pet profile.',
    },
    {
      key: 'monthly_subscription',
      heading: 'Monthly subscription',
      body: 'Reserved for future recurring plan logic and subscription ranking.',
    },
    {
      key: 'bundles',
      heading: 'Bundles',
      body: 'Reserved for future bundle recommendations built from matched products.',
    },
    {
      key: 'complete_plan',
      heading: 'Complete plan',
      body: 'Reserved for future full-plan packaging across meals, treats, and add-ons.',
    },
  ];

  function getThemeConfig() {
    return window.PlenniaThemeConfig || {};
  }

  function parseJSON(value, fallback) {
    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  }

  function readStorage(key, fallback) {
    try {
      const value = window.localStorage.getItem(key);
      return value ? parseJSON(value, fallback) : fallback;
    } catch (error) {
      return fallback;
    }
  }

  function writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      return;
    }
  }

  function removeStorage(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      return;
    }
  }

  function arrayify(value) {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (value === undefined || value === null || value === '') return [];
    return [value];
  }

  function uniq(items) {
    return [...new Set(arrayify(items).flat().filter(Boolean))];
  }

  function normalizeChoiceList(items) {
    const normalized = uniq(items)
      .map((item) => normalizeToken(item))
      .filter(Boolean);

    if (normalized.length > 1) return normalized.filter((item) => item !== 'none');
    return normalized;
  }

  function normalizeToken(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^\w\s/+-]+/g, '')
      .replace(/[\/+\s-]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  function capitalize(value) {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  function labelFromMap(group, key, fallback = '') {
    return PLAN_CONFIG.labels[group]?.[key] || fallback || String(key || '').replace(/_/g, ' ');
  }

  function readableList(items) {
    const list = arrayify(items).filter(Boolean);
    if (!list.length) return '';
    if (list.length === 1) return list[0];
    if (list.length === 2) return `${list[0]} and ${list[1]}`;
    return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
  }

  function stripHtml(value) {
    return String(value || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function normalizeProductTags(tags) {
    if (Array.isArray(tags)) return tags.map((tag) => String(tag).trim().toLowerCase()).filter(Boolean);

    return String(tags || '')
      .split(',')
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean);
  }

  function debugLogProductCatalog(source, products) {
    if (!window.console) return;

    const normalizedProducts = normalizePlanProductList(products);
    console.info(`[Plennia Quiz] ${source}: ${normalizedProducts.length} products loaded`);
    console.info(
      '[Plennia Quiz] Product tags',
      normalizedProducts.map((product) => ({
        title: product.title,
        handle: product.handle,
        tags: arrayify(product.tags),
      }))
    );
  }

  function readJSONScript(scope, selector, fallback) {
    const node = scope.querySelector(selector);
    if (!node) return fallback;
    return parseJSON(node.textContent, fallback);
  }

  function getInitialActivePetId(scope = document) {
    return String(readJSONScript(scope, '[data-initial-active-pet]', '') || '').trim();
  }

  function getProductsFromScripts(scope = document) {
    const selectors = [
      '[data-plennia-dashboard-products]',
      '[data-plennia-plan-products]',
      '[data-plennia-recommendation-products]',
      '[data-plennia-quiz-products]',
    ];

    for (const selector of selectors) {
      const products = readJSONScript(scope, selector, null);
      if (Array.isArray(products) && products.length) return products;
    }

    return [];
  }

  function formatMoney(cents, currencyCode) {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode || 'USD',
      }).format(amount);
    } catch (error) {
      return `${amount.toFixed(2)} ${currencyCode || 'USD'}`;
    }
  }

  function iconForPetType(type) {
    return type === 'cat' ? 'CAT' : 'DOG';
  }

  function currentCustomerIdFromDocument() {
    const accountNode = document.querySelector('[data-customer-id]');
    return accountNode ? accountNode.dataset.customerId : '';
  }

  function currentCustomerEmailFromDocument() {
    const accountNode = document.querySelector('[data-customer-email]');
    return accountNode ? accountNode.dataset.customerEmail || '' : '';
  }

  function getCustomerPetsSyncVersion() {
    return String(getThemeConfig().customerPetsSyncVersion || CUSTOMER_PETS_SYNC_VERSION).trim();
  }

  function getCustomerPetsStorageConfig() {
    return {
      namespace: String(getThemeConfig().customerPetsNamespace || CUSTOMER_PETS_STORAGE_DEFAULTS.namespace).trim(),
      petsKey: String(getThemeConfig().customerPetsMetafieldKey || CUSTOMER_PETS_STORAGE_DEFAULTS.petsKey).trim(),
      activePetKey: String(
        getThemeConfig().customerActivePetMetafieldKey || CUSTOMER_PETS_STORAGE_DEFAULTS.activePetKey
      ).trim(),
    };
  }

  function getQuizContext() {
    return readStorage(STORAGE_KEYS.quizContext, {});
  }

  function setQuizContext(context) {
    writeStorage(STORAGE_KEYS.quizContext, context || {});
  }

  function clearQuizContext() {
    removeStorage(STORAGE_KEYS.quizContext);
  }

  function getPetStorageKeys(customerId) {
    return {
      pets: `${STORAGE_KEYS.pets}:${customerId || 'guest'}`,
      activePet: `${STORAGE_KEYS.activePet}:${customerId || 'guest'}`,
    };
  }

  function getCustomerPetsEndpoint() {
    return String(getThemeConfig().customerPetsEndpoint || '').trim();
  }

  function customerPetsStorageMessage() {
    if (getCustomerPetsEndpoint()) {
      return 'Pet profiles load from Shopify customer data and stay synced through the configured storefront endpoint.';
    }

    return 'Pet profiles save in this browser right now. To persist them in Shopify customer metafields or metaobjects across devices, connect an app proxy endpoint in Theme settings.';
  }

  function buildCollectionProductsUrl(handle, page) {
    const collectionHandle = String(handle || 'all').trim() || 'all';
    const pathname =
      collectionHandle === 'all'
        ? '/products.json'
        : `/collections/${encodeURIComponent(collectionHandle)}/products.json`;
    const url = new URL(pathname, window.location.origin);
    url.searchParams.set('limit', String(PRODUCT_FETCH_LIMIT));
    url.searchParams.set('page', String(page));
    return url.toString();
  }

  function normalizeAjaxProduct(product) {
    if (!product || typeof product !== 'object') return null;

    const variants = arrayify(product.variants);
    const preferredVariant = variants.find((variant) => variant && variant.available) || variants[0] || {};
    const productPrice = Number(preferredVariant.price || product.price || 0);
    const compareAtPrice = Number(preferredVariant.compare_at_price || product.compare_at_price || 0);
    const productImage =
      product.featured_image ||
      product.image?.src ||
      (Array.isArray(product.images) && product.images[0] && product.images[0].src) ||
      '';

    // Extract selling plan from first available group (populated by selling plan apps)
    const sellingPlanGroups = arrayify(product.selling_plan_groups || preferredVariant.selling_plan_allocations);
    const firstPlanAllocation = sellingPlanGroups[0];
    const sellingPlanId = firstPlanAllocation?.selling_plan?.id
      || firstPlanAllocation?.selling_plan_id
      || null;
    const subscriptionDiscount = (() => {
      const priceAdjustments = arrayify(firstPlanAllocation?.price_adjustments || firstPlanAllocation?.selling_plan?.price_adjustments);
      const adj = priceAdjustments[0];
      if (adj?.value_type === 'percentage' && adj?.value) return Number(adj.value);
      return DEFAULT_SUBSCRIPTION_DISCOUNT;
    })();

    return {
      id: product.id,
      handle: product.handle || '',
      title: product.title || 'Untitled product',
      url: product.url || (product.handle ? `/products/${product.handle}` : '#'),
      tags: normalizeProductTags(product.tags),
      price: Math.round(productPrice * 100),
      compare_at_price: compareAtPrice ? Math.round(compareAtPrice * 100) : 0,
      description: stripHtml(product.body_html || product.description || '').split(/\s+/).slice(0, 20).join(' '),
      featured_image: productImage,
      variant_id: preferredVariant.id || null,
      available: preferredVariant.available !== false,
      selling_plan_id: sellingPlanId,
      subscription_discount: subscriptionDiscount,
    };
  }

  function fetchCollectionProducts(handle) {
    const collectionHandle = String(handle || 'all').trim() || 'all';

    if (!productCollectionCache.has(collectionHandle)) {
      const request = (async () => {
        const products = [];
        let page = 1;

        while (page <= 20) {
          const response = await fetch(buildCollectionProductsUrl(collectionHandle, page), {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error('Unable to fetch collection products');

          const payload = await response.json();
          const batch = arrayify(payload.products).map(normalizeAjaxProduct).filter(Boolean);
          products.push(...batch);

          if (batch.length < PRODUCT_FETCH_LIMIT) break;
          page += 1;
        }

        debugLogProductCatalog(`fetchCollectionProducts(${collectionHandle})`, products);
        return products;
      })().catch((error) => {
        productCollectionCache.delete(collectionHandle);
        throw error;
      });

      productCollectionCache.set(collectionHandle, request);
    }

    return productCollectionCache.get(collectionHandle);
  }

  function resolveProductHandle(product) {
    if (!product) return '';
    if (product.handle) return String(product.handle);
    const url = String(product.url || '');
    const matched = url.match(/\/products\/([^/?#]+)/);
    return matched ? matched[1] : '';
  }

  function normalizePlanProduct(product) {
    if (!product || typeof product !== 'object') return null;

    const price = Number(product.price || 0);
    const compareAtPrice = Number(product.compare_at_price || product.compareAtPrice || 0);

    return {
      id: product.id,
      handle: resolveProductHandle(product),
      title: product.title || 'Untitled product',
      url: product.url || (resolveProductHandle(product) ? `/products/${resolveProductHandle(product)}` : '#'),
      tags: normalizeProductTags(product.tags),
      price: Number.isFinite(price) ? price : 0,
      compare_at_price: Number.isFinite(compareAtPrice) ? compareAtPrice : 0,
      description: stripHtml(product.description || product.body_html || '').split(/\s+/).slice(0, 24).join(' '),
      featured_image: product.featured_image || product.image || '',
      variant_id: Number(product.variant_id || product.variantId || 0) || null,
      available: product.available !== false,
      selling_plan_id: product.selling_plan_id || null,
      subscription_discount: product.subscription_discount || DEFAULT_SUBSCRIPTION_DISCOUNT,
    };
  }

  function normalizePlanProductList(products) {
    return arrayify(products).map(normalizePlanProduct).filter(Boolean);
  }

  function mapAgeGroup(petType, ageValue) {
    const age = Number(ageValue);
    if (Number.isNaN(age)) return '';
    if (petType === 'dog') {
      if (age < 1) return 'puppy';
      if (age < 7) return 'adult';
      return 'senior';
    }
    if (petType === 'cat') {
      if (age < 1) return 'kitten';
      if (age < 8) return 'adult';
      return 'senior';
    }
    return '';
  }

  function activityScoreForLevel(level) {
    if (level === 'low') return 2;
    if (level === 'high') return 5;
    return 3;
  }

  function activityStepForScore(score) {
    const normalizedScore = Math.min(5, Math.max(1, Number(score || 3)));
    return PLAN_ACTIVITY_STEPS.find((step) => step.score === normalizedScore) || PLAN_ACTIVITY_STEPS[2];
  }

  function normalizeQuizState(state) {
    const petType = normalizeToken(state.petType || state.pet_type || state.type);
    const petName = String(state.petName || state.pet_name || state.name || '').trim();
    const petSex = normalizeToken(state.petSex || state.pet_sex || state.sex);
    const petAge = String(state.petAge || state.pet_age || state.age || '').trim();
    const petBreed = String(state.petBreed || state.pet_breed || state.breed || '').trim();
    const petNeutered = normalizeToken(
      state.petNeutered || state.pet_neutered || state.neuteredStatus || state.neutered_status
    );
    const petWeight = String(state.petWeight || state.pet_weight || state.weight || '').trim();
    const bodyCondition = normalizeToken(state.bodyCondition || state.body_condition);
    const activityLevel = normalizeToken(state.activityLevel || state.activity_level);
    const activityScore = Number(state.activityScore || state.activity_score || activityScoreForLevel(activityLevel));
    const currentFoodTypes = normalizeChoiceList(
      state.currentFoodTypes || state.current_food_types || state.currentFood || state.current_food || []
    );
    const primaryGoals = normalizeChoiceList(
      state.primaryGoals ||
        state.primary_goals ||
        state.focusGoals ||
        state.focus_goals ||
        state.primaryGoal ||
        state.primary_goal ||
        state.focusGoal ||
        state.focus_goal ||
        state.goals ||
        state.goal ||
        []
    );
    const primaryGoal = primaryGoals[0] || '';
    const healthIssues = normalizeChoiceList(
      state.healthIssues || state.health_issues || state.healthProblems || state.health_problems || []
    );
    const ingredientAllergies = normalizeChoiceList(
      state.ingredientAllergies || state.ingredient_allergies || state.allergies || []
    );
    const petId = String(state.petId || state.pet_id || state.id || '').trim();
    const petAgeGroup =
      normalizeToken(state.petAgeGroup || state.pet_age_group || state.ageGroup) || mapAgeGroup(petType, petAge);
    const recommendedProductHandles = uniq(
      state.recommendedProductHandles || state.recommended_product_handles || []
    ).map(String);
    const lastQuizCompletedAt = state.lastQuizCompletedAt || state.last_quiz_completed_at || '';
    const email = String(state.email || '').trim();

    return {
      petId,
      pet_id: petId,
      petType,
      pet_type: petType,
      petName,
      pet_name: petName,
      petSex,
      pet_sex: petSex,
      sex: petSex,
      petAge,
      pet_age: petAge,
      age: petAge,
      petAgeGroup,
      pet_age_group: petAgeGroup,
      ageGroup: petAgeGroup,
      petBreed,
      pet_breed: petBreed,
      breed: petBreed,
      petNeutered,
      pet_neutered: petNeutered,
      neuteredStatus: petNeutered,
      petWeight,
      pet_weight: petWeight,
      weight: petWeight,
      bodyCondition,
      body_condition: bodyCondition,
      activityLevel: activityLevel || activityStepForScore(activityScore).level,
      activity_level: activityLevel || activityStepForScore(activityScore).level,
      activityScore,
      activity_score: activityScore,
      currentFoodTypes,
      current_food_types: currentFoodTypes,
      currentFood: currentFoodTypes,
      current_food: currentFoodTypes,
      primaryGoals,
      primary_goals: primaryGoals,
      primaryGoal,
      primary_goal: primaryGoal,
      goals: primaryGoals,
      healthIssues,
      health_issues: healthIssues,
      healthProblems: healthIssues,
      health_problems: healthIssues,
      ingredientAllergies,
      ingredient_allergies: ingredientAllergies,
      allergies: ingredientAllergies,
      email,
      recommendedProductHandles,
      recommended_product_handles: recommendedProductHandles,
      lastQuizCompletedAt,
      last_quiz_completed_at: lastQuizCompletedAt,
    };
  }

  function getQuizState(storageKey = STORAGE_KEYS.quiz) {
    return normalizeQuizState(readStorage(storageKey, {}));
  }

  function setQuizState(state, storageKey = STORAGE_KEYS.quiz) {
    const normalized = normalizeQuizState(state);
    writeStorage(storageKey, normalized);
    document.dispatchEvent(new CustomEvent('plennia:quiz:updated', { detail: { state: normalized } }));
    return normalized;
  }

  function pronounsForState(state) {
    const sex = normalizeToken(state.petSex || state.sex);
    if (sex === 'male') return { subject: 'He', object: 'him', possessive: 'his' };
    if (sex === 'female') return { subject: 'She', object: 'her', possessive: 'her' };
    return { subject: 'They', object: 'them', possessive: 'their' };
  }

  function hasTag(product, tags) {
    return arrayify(tags).some((tag) => arrayify(product.tags).includes(String(tag).toLowerCase()));
  }

  function inferFormat(product) {
    if (hasTag(product, PLAN_CONFIG.tags.formats.treat)) return 'treat';
    if (hasTag(product, PLAN_CONFIG.tags.formats.wet)) return 'wet';
    if (hasTag(product, PLAN_CONFIG.tags.formats.dry)) return 'dry';
    return 'other';
  }

  function inferRole(product) {
    if (hasTag(product, PLAN_CONFIG.tags.roles.main)) return 'main';
    if (hasTag(product, PLAN_CONFIG.tags.roles.support)) return 'support';
    if (hasTag(product, PLAN_CONFIG.tags.roles.addon)) return 'addon';
    if (inferFormat(product) === 'treat') return 'addon';
    if (['dry', 'wet'].includes(inferFormat(product))) return 'main';
    return 'other';
  }

  function inferBenefits(product) {
    return Object.keys(PLAN_CONFIG.tags.benefits).filter((key) => hasTag(product, PLAN_CONFIG.tags.benefits[key]));
  }

  function buildNeeds(state) {
    const normalizedState = normalizeQuizState(state);
    const goalBenefits = uniq(
      normalizedState.primaryGoals.map((goal) => PLAN_CONFIG.goalBenefits[goal] || [])
    );
    const healthBenefits = uniq(
      normalizedState.healthIssues.map((issue) => PLAN_CONFIG.healthBenefits[issue] || [])
    );

    return {
      state: normalizedState,
      primaryBenefits: goalBenefits,
      secondaryBenefits: healthBenefits,
      desiredBenefits: uniq([...goalBenefits, ...healthBenefits]),
      currentFormats: normalizedState.currentFoodTypes.map((item) => (item === 'treats' ? 'treat' : item)),
      isDog: normalizedState.petType === 'dog',
      isSenior: normalizedState.petAgeGroup === 'senior',
      isAdultOrSenior: ['adult', 'senior'].includes(normalizedState.petAgeGroup),
      hasSkinNeed:
        goalBenefits.includes('skin_coat') ||
        normalizedState.healthIssues.includes('skin_hair') ||
        normalizedState.healthIssues.includes('shedding'),
      hasJointNeed:
        goalBenefits.includes('joint') || normalizedState.healthIssues.includes('joint_problems'),
      hasDentalNeed:
        goalBenefits.includes('dental') || normalizedState.healthIssues.includes('dental'),
      hasWeightNeed:
        goalBenefits.includes('weight') ||
        normalizedState.healthIssues.includes('obesity') ||
        normalizedState.bodyCondition === 'overweight',
    };
  }

  function shouldRecommendSpeciesCatalog(state) {
    const normalizedState = normalizeQuizState(state);
    const goals = normalizedState.primaryGoals.filter((item) => item && item !== 'none');
    const issues = normalizedState.healthIssues.filter((item) => item && item !== 'none');

    return (
      normalizedState.primaryGoals.includes('none') ||
      normalizedState.healthIssues.includes('none') ||
      (!goals.length && !issues.length)
    );
  }

  function filterProducts(products, state) {
    const normalizedProducts = normalizePlanProductList(products);
    const normalizedState = normalizeQuizState(state);
    const messages = [];

    if (!normalizedState.petType) {
      return {
        products: normalizedProducts,
        messages,
        petFallbackUsed: false,
        storeFallbackUsed: normalizedProducts.length > 0,
      };
    }

    const speciesMatches = normalizedProducts.filter((product) =>
      hasTag(product, PLAN_CONFIG.tags.species[normalizedState.petType] || [])
    );
    if (!speciesMatches.length) {
      if (normalizedProducts.length) {
        messages.push(`No ${normalizedState.petType} tags were detected, so we used the closest products currently available in the store.`);
      }

      return {
        products: normalizedProducts,
        messages,
        petFallbackUsed: false,
        storeFallbackUsed: normalizedProducts.length > 0,
      };
    }

    const allergySelections = normalizedState.ingredientAllergies.filter((item) => item !== 'none');
    if (!allergySelections.length) {
      return {
        products: speciesMatches,
        messages,
        petFallbackUsed: false,
        storeFallbackUsed: false,
        allergyFallbackUsed: false,
      };
    }

    const allergyFiltered = speciesMatches.filter(
      (product) =>
        !allergySelections.some((allergy) => hasTag(product, PLAN_CONFIG.tags.proteins[allergy] || []))
    );

    if (allergyFiltered.length) {
      return {
        products: allergyFiltered,
        messages,
        petFallbackUsed: false,
        storeFallbackUsed: false,
        allergyFallbackUsed: false,
      };
    }

    messages.push('We could not fully match allergy preferences yet, but here are the closest products for your pet.');

    return {
      products: speciesMatches,
      messages,
      petFallbackUsed: true,
      storeFallbackUsed: false,
      allergyFallbackUsed: true,
    };
  }

  function scoreProduct(product, state, needs) {
    if (!product) return null;

    const normalizedState = normalizeQuizState(state);
    const format = inferFormat(product);
    const role = inferRole(product);
    const benefits = inferBenefits(product);
    const reasons = [];
    let score = PLAN_CONFIG.scoring.species;

    if (normalizedState.petAgeGroup && hasTag(product, PLAN_CONFIG.tags.age[normalizedState.petAgeGroup] || [])) {
      score += PLAN_CONFIG.scoring.age;
      reasons.push(labelFromMap('age_group', normalizedState.petAgeGroup));
    }

    if (needs.primaryBenefits.some((benefit) => benefits.includes(benefit))) {
      score += PLAN_CONFIG.scoring.primaryBenefit;
      const matchedGoal = normalizedState.primaryGoals.find((goal) =>
        (PLAN_CONFIG.goalBenefits[goal] || []).some((benefit) => benefits.includes(benefit))
      );
      reasons.push(labelFromMap('primary_goal', matchedGoal || normalizedState.primaryGoal || needs.primaryBenefits[0]));
    }

    needs.secondaryBenefits.forEach((benefit) => {
      if (benefits.includes(benefit)) score += PLAN_CONFIG.scoring.secondaryBenefit;
    });

    if (needs.currentFormats.includes(format)) {
      score += PLAN_CONFIG.scoring.currentFormat;
      reasons.push(labelFromMap('food_types', format === 'treat' ? 'treats' : format));
    }

    if (role === 'main') score += PLAN_CONFIG.scoring.roleMain;
    if (role === 'support') score += PLAN_CONFIG.scoring.roleSupport;
    if (role === 'addon') score += PLAN_CONFIG.scoring.roleAddon;

    if (needs.hasWeightNeed) {
      if (format === 'dry') score += PLAN_CONFIG.scoring.dryWeightBoost;
      if (format === 'wet') score -= PLAN_CONFIG.scoring.wetWeightPenalty;
    }

    if (needs.isSenior && benefits.includes('joint')) score += PLAN_CONFIG.scoring.seniorJointBoost;
    if (needs.isSenior && needs.hasJointNeed && benefits.includes('joint')) score += PLAN_CONFIG.scoring.seniorJointPriority;

    if (needs.isAdultOrSenior && needs.hasSkinNeed && benefits.includes('skin_coat')) {
      if (role === 'main') score += PLAN_CONFIG.scoring.skinMainPriority;
      if (format === 'treat' || role === 'addon') score += PLAN_CONFIG.scoring.skinTreatPriority;
    }

    if (needs.isDog && needs.isAdultOrSenior && needs.hasDentalNeed && benefits.includes('dental')) {
      if (format === 'treat' || role === 'addon') score += PLAN_CONFIG.scoring.dentalDogTreatPriority;
      if (role === 'main' || ['dry', 'wet'].includes(format)) score += PLAN_CONFIG.scoring.dentalDogFoodPriority;
    }

    if (hasTag(product, PLAN_CONFIG.tags.priority.high)) score += PLAN_CONFIG.scoring.priorityHigh;
    if (hasTag(product, PLAN_CONFIG.tags.priority.medium)) score += PLAN_CONFIG.scoring.priorityMedium;
    if (hasTag(product, PLAN_CONFIG.tags.priority.hero)) score += PLAN_CONFIG.scoring.hero;
    if (hasTag(product, PLAN_CONFIG.tags.priority.bestseller)) score += PLAN_CONFIG.scoring.bestseller;

    return {
      ...product,
      score,
      format,
      role,
      benefits,
      reasons: uniq(reasons).slice(0, 4),
    };
  }

  function productMatchesBenefit(product, benefit) {
    return inferBenefits(product).includes(benefit);
  }

  function pickProduct(products, usedIds, predicate) {
    return arrayify(products).find((product) => !usedIds.has(product.id) && predicate(product)) || null;
  }

  function chooseSlots(rankedProducts, needs) {
    const usedIds = new Set();
    const slots = { primary: null, secondary: null, addon: null };

    function assign(slotKey, predicate) {
      const found = pickProduct(rankedProducts, usedIds, predicate);
      if (found) {
        slots[slotKey] = found;
        usedIds.add(found.id);
      }
      return found;
    }

    if (needs.isDog && needs.isAdultOrSenior && needs.hasDentalNeed) {
      assign('primary', (product) => (product.format === 'treat' || product.role === 'addon') && productMatchesBenefit(product, 'dental'));
      assign('secondary', (product) => product.role === 'main' && productMatchesBenefit(product, 'dental'));
      assign('addon', (product) => (product.format === 'treat' || product.role === 'addon') && productMatchesBenefit(product, 'dental'));
    }

    if (!slots.primary && needs.isAdultOrSenior && needs.hasSkinNeed) {
      assign('primary', (product) => product.role === 'main' && productMatchesBenefit(product, 'skin_coat'));
      assign('addon', (product) => (product.format === 'treat' || product.role === 'addon') && productMatchesBenefit(product, 'skin_coat'));
    }

    if (!slots.primary && needs.isSenior && needs.hasJointNeed) {
      assign('primary', (product) => product.role === 'main' && productMatchesBenefit(product, 'joint'));
      assign('addon', (product) => (product.format === 'treat' || product.role === 'addon') && productMatchesBenefit(product, 'joint'));
    }

    if (!slots.primary) {
      assign(
        'primary',
        (product) =>
          product.role === 'main' && needs.desiredBenefits.some((benefit) => productMatchesBenefit(product, benefit))
      );
    }

    if (!slots.primary) assign('primary', (product) => product.role === 'main');
    if (!slots.primary) assign('primary', () => true);

    if (!slots.secondary) {
      assign(
        'secondary',
        (product) =>
          product.role === 'main' &&
          (needs.desiredBenefits.some((benefit) => productMatchesBenefit(product, benefit)) ||
            needs.currentFormats.includes(product.format))
      );
    }

    if (!slots.secondary) assign('secondary', (product) => product.role === 'main' || product.role === 'support');
    if (!slots.secondary) assign('secondary', () => true);

    if (!slots.addon) {
      assign(
        'addon',
        (product) =>
          (product.format === 'treat' || product.role === 'addon') &&
          needs.desiredBenefits.some((benefit) => productMatchesBenefit(product, benefit))
      );
    }

    if (!slots.addon) assign('addon', (product) => product.format === 'treat' || product.role === 'addon');
    if (!slots.addon) assign('addon', (product) => product.role === 'support');
    if (!slots.addon) assign('addon', () => true);

    return slots;
  }

  function buildRecommendationGroups(rankedProducts, state) {
    const normalizedState = normalizeQuizState(state);
    const preferredFormats = uniq(
      normalizedState.currentFoodTypes.map((item) => (item === 'treats' ? 'treat' : item))
    ).filter((format) => ['dry', 'wet', 'treat'].includes(format));
    const orderedFormats = uniq([...preferredFormats, 'dry', 'wet', 'treat']);

    return orderedFormats.map((format) => ({
      key: format,
      ...PLAN_GROUP_META[format],
      products: rankedProducts.filter((product) => product.format === format),
    }));
  }

  function buildFutureRecommendationSlots() {
    return PLAN_FUTURE_SLOT_META.map((slot) => ({ ...slot, ready: false }));
  }

  function recommendProducts(products, state) {
    const normalizedState = normalizeQuizState(state);
    const needs = buildNeeds(normalizedState);
    const normalizedProducts = normalizePlanProductList(products);
    const filtered = filterProducts(products, normalizedState);

    if (!normalizedState.petType || !filtered.products.length) {
      return {
        state: normalizedState,
        slots: { primary: null, secondary: null, addon: null },
        groups: buildRecommendationGroups([], normalizedState),
        futureSlots: buildFutureRecommendationSlots(),
        rankedProducts: [],
        messages: filtered.messages,
        recommendedHandles: [],
        hasResults: false,
      };
    }

    const messages = arrayify(filtered.messages);
    const speciesWideMode = shouldRecommendSpeciesCatalog(normalizedState);
    const hasExactGoalMatch =
      speciesWideMode ||
      !needs.desiredBenefits.length ||
      filtered.products.some((product) => needs.desiredBenefits.some((benefit) => productMatchesBenefit(product, benefit)));

    if (!hasExactGoalMatch) {
      messages.push('We did not find an exact goal-tag match yet, so these are the closest products for your pet.');
    }

    let rankedProducts = filtered.products
      .map((product) => scoreProduct(product, normalizedState, needs))
      .filter(Boolean)
      .sort((first, second) => second.score - first.score || first.title.localeCompare(second.title));

    if (!rankedProducts.length && normalizedState.petType) {
      const petFallbackProducts = normalizedProducts
        .filter((product) => hasTag(product, PLAN_CONFIG.tags.species[normalizedState.petType] || []))
        .map((product) => scoreProduct(product, normalizedState, needs))
        .filter(Boolean)
        .sort((first, second) => second.score - first.score || first.title.localeCompare(second.title));

      if (petFallbackProducts.length) {
        rankedProducts = petFallbackProducts;
        messages.push(`No exact filtered matches were left, so we returned all ${normalizedState.petType} products.`);
      }
    }

    if (!rankedProducts.length) {
      rankedProducts = normalizedProducts
        .map((product) => scoreProduct(product, normalizedState, needs))
        .filter(Boolean)
        .sort((first, second) => second.score - first.score || first.title.localeCompare(second.title));

      if (rankedProducts.length) {
        messages.push('No pet-specific matches were left, so we returned all products currently available in the store.');
      }
    }

    const groups = buildRecommendationGroups(rankedProducts, normalizedState);
    const slots = chooseSlots(rankedProducts, needs);
    const recommendedHandles = uniq(
      groups
        .flatMap((group) => group.products)
        .map((product) => resolveProductHandle(product))
        .filter(Boolean)
    );

    return {
      state: normalizedState,
      slots,
      groups,
      futureSlots: buildFutureRecommendationSlots(),
      rankedProducts,
      messages,
      recommendedHandles,
      hasResults: rankedProducts.length > 0,
    };
  }

  function buildSummaryChips(state) {
    const normalizedState = normalizeQuizState(state);
    const chips = [];

    if (normalizedState.petType) chips.push(capitalize(normalizedState.petType));
    if (normalizedState.petName) chips.push(normalizedState.petName);
    if (normalizedState.petAgeGroup) chips.push(labelFromMap('age_group', normalizedState.petAgeGroup));
    if (normalizedState.petBreed) chips.push(normalizedState.petBreed);
    if (normalizedState.bodyCondition) chips.push(labelFromMap('body_condition', normalizedState.bodyCondition));
    if (normalizedState.activityLevel) chips.push(labelFromMap('activity_level', normalizedState.activityLevel));
    normalizedState.primaryGoals
      .filter((item) => item !== 'none')
      .forEach((item) => chips.push(labelFromMap('primary_goal', item)));
    normalizedState.currentFoodTypes.forEach((item) => chips.push(labelFromMap('food_types', item)));
    normalizedState.healthIssues
      .filter((item) => item !== 'none')
      .forEach((item) => chips.push(labelFromMap('health_issues', item)));
    return uniq(chips);
  }

  function buildResultsCopy(state, recommendation) {
    const normalizedState = normalizeQuizState(state);
    const petName = normalizedState.petName || 'Your pet';
    const pronouns = pronounsForState(normalizedState);
    const goalLabels = normalizedState.primaryGoals
      .filter((item) => item !== 'none')
      .map((item) => labelFromMap('primary_goal', item));
    const goalLabel = goalLabels.length ? readableList(goalLabels) : 'general wellbeing';
    const ageLabel = normalizedState.petAgeGroup
      ? labelFromMap('age_group', normalizedState.petAgeGroup).toLowerCase()
      : 'current';
    const foodLabel =
      readableList(
        normalizedState.currentFoodTypes.map((item) => labelFromMap('food_types', item).toLowerCase())
      ) || 'current feeding habits';

    return {
      title: `Recommended for ${petName}`,
      body: `Built around ${pronouns.possessive} ${ageLabel} needs, ${goalLabel.toLowerCase()}, and ${foodLabel}.`,
      status:
        arrayify(recommendation?.messages).length > 0
          ? recommendation.messages[0]
          : `The catalog was ranked against real Shopify tags for ${petName}.`,
    };
  }

  function serializeProduct(product) {
    if (!product) return null;
    return {
      id: product.id,
      handle: resolveProductHandle(product),
      title: product.title,
      url: product.url,
      price: product.price,
      compare_at_price: product.compare_at_price,
      featured_image: product.featured_image,
      variant_id: product.variant_id,
      available: product.available,
      score: product.score,
      format: product.format,
      role: product.role,
      reasons: arrayify(product.reasons),
      benefits: arrayify(product.benefits),
      tags: arrayify(product.tags),
    };
  }

  function normalizePetProfile(profile) {
    if (!profile || typeof profile !== 'object') return null;

    const savedQuizAnswers = normalizeQuizState(profile.savedQuizAnswers || profile.saved_quiz_answers || profile);
    const petId = String(profile.pet_id || profile.petId || profile.id || savedQuizAnswers.petId || `pet-${Date.now()}`).trim();
    const petType = normalizeToken(profile.pet_type || profile.petType || profile.type || savedQuizAnswers.petType) || 'dog';
    const petName = String(profile.pet_name || profile.petName || profile.name || savedQuizAnswers.petName || '').trim();
    const primaryGoals = normalizeChoiceList(
      profile.primary_goals ||
        profile.primaryGoals ||
        profile.primary_goal ||
        profile.primaryGoal ||
        savedQuizAnswers.primaryGoals ||
        savedQuizAnswers.primaryGoal ||
        []
    );
    const primaryGoal = primaryGoals[0] || '';
    const healthIssues = normalizeChoiceList(
      profile.health_issues || profile.healthIssues || profile.healthProblems || savedQuizAnswers.healthIssues || []
    );
    const ingredientAllergies = normalizeChoiceList(
      profile.ingredient_allergies ||
        profile.ingredientAllergies ||
        profile.allergies ||
        savedQuizAnswers.ingredientAllergies ||
        []
    );

    return {
      id: petId,
      pet_id: petId,
      petId,
      name: petName,
      petName,
      pet_name: petName,
      type: petType,
      petType: petType,
      pet_type: petType,
      sex: normalizeToken(profile.pet_sex || profile.petSex || profile.sex || savedQuizAnswers.petSex),
      petSex: normalizeToken(profile.pet_sex || profile.petSex || profile.sex || savedQuizAnswers.petSex),
      age: String(profile.pet_age || profile.petAge || profile.age || savedQuizAnswers.petAge || '').trim(),
      petAge: String(profile.pet_age || profile.petAge || profile.age || savedQuizAnswers.petAge || '').trim(),
      ageGroup:
        normalizeToken(profile.pet_age_group || profile.petAgeGroup || profile.ageGroup || savedQuizAnswers.petAgeGroup) ||
        mapAgeGroup(petType, profile.pet_age || profile.age || savedQuizAnswers.petAge),
      petAgeGroup:
        normalizeToken(profile.pet_age_group || profile.petAgeGroup || profile.ageGroup || savedQuizAnswers.petAgeGroup) ||
        mapAgeGroup(petType, profile.pet_age || profile.age || savedQuizAnswers.petAge),
      breed: String(profile.pet_breed || profile.petBreed || profile.breed || savedQuizAnswers.petBreed || '').trim(),
      petBreed: String(profile.pet_breed || profile.petBreed || profile.breed || savedQuizAnswers.petBreed || '').trim(),
      neuteredStatus: normalizeToken(
        profile.pet_neutered ||
          profile.petNeutered ||
          profile.neuteredStatus ||
          profile.neutered_status ||
          savedQuizAnswers.petNeutered
      ),
      petNeutered: normalizeToken(
        profile.pet_neutered ||
          profile.petNeutered ||
          profile.neuteredStatus ||
          profile.neutered_status ||
          savedQuizAnswers.petNeutered
      ),
      weight: String(profile.pet_weight || profile.petWeight || profile.weight || savedQuizAnswers.petWeight || '').trim(),
      petWeight: String(profile.pet_weight || profile.petWeight || profile.weight || savedQuizAnswers.petWeight || '').trim(),
      bodyCondition: normalizeToken(profile.body_condition || profile.bodyCondition || savedQuizAnswers.bodyCondition),
      activityLevel: normalizeToken(profile.activity_level || profile.activityLevel || savedQuizAnswers.activityLevel),
      currentFoodTypes: normalizeChoiceList(
        profile.current_food_types || profile.currentFoodTypes || savedQuizAnswers.currentFoodTypes || []
      ),
      primaryGoals,
      primary_goals: primaryGoals,
      primaryGoal,
      primary_goal: primaryGoal,
      goals: primaryGoals,
      healthIssues,
      healthProblems: healthIssues,
      ingredientAllergies,
      allergies: ingredientAllergies,
      recommendedProductHandles: uniq(
        profile.recommended_product_handles ||
          profile.recommendedProductHandles ||
          savedQuizAnswers.recommendedProductHandles ||
          []
      ),
      lastQuizCompletedAt:
        profile.last_quiz_completed_at || profile.lastQuizCompletedAt || savedQuizAnswers.lastQuizCompletedAt || '',
      email: String(profile.email || savedQuizAnswers.email || '').trim(),
      savedQuizAnswers,
      savedRecommendedPlan: profile.savedRecommendedPlan || profile.saved_recommended_plan || null,
      createdAt: profile.createdAt || profile.created_at || new Date().toISOString(),
      updatedAt: profile.updatedAt || profile.updated_at || new Date().toISOString(),
    };
  }

  function normalizePetProfiles(profiles) {
    return arrayify(profiles).map(normalizePetProfile).filter(Boolean);
  }

  function buildQuizStateFromPetProfile(profile) {
    const normalizedProfile = normalizePetProfile(profile);
    if (!normalizedProfile) return normalizeQuizState({});

    return normalizeQuizState({
      ...(normalizedProfile.savedQuizAnswers || {}),
      pet_id: normalizedProfile.id,
      pet_type: normalizedProfile.petType,
      pet_name: normalizedProfile.petName,
      pet_sex: normalizedProfile.petSex,
      pet_age: normalizedProfile.petAge,
      pet_age_group: normalizedProfile.petAgeGroup,
      pet_breed: normalizedProfile.petBreed,
      pet_neutered: normalizedProfile.petNeutered,
      pet_weight: normalizedProfile.petWeight,
      body_condition: normalizedProfile.bodyCondition,
      activity_level: normalizedProfile.activityLevel,
      current_food_types: normalizedProfile.currentFoodTypes,
      primary_goals: normalizedProfile.primaryGoals,
      primary_goal: normalizedProfile.primaryGoal,
      health_issues: normalizedProfile.healthIssues,
      ingredient_allergies: normalizedProfile.ingredientAllergies,
      recommended_product_handles: normalizedProfile.recommendedProductHandles,
      last_quiz_completed_at: normalizedProfile.lastQuizCompletedAt,
      email: normalizedProfile.email,
    });
  }

  function profileTimestampValue(value) {
    const timestamp = Date.parse(value || '');
    return Number.isFinite(timestamp) ? timestamp : 0;
  }

  function petProfileFreshness(profile) {
    const normalizedProfile = normalizePetProfile(profile);
    if (!normalizedProfile) return 0;
    return Math.max(
      profileTimestampValue(normalizedProfile.updatedAt),
      profileTimestampValue(normalizedProfile.lastQuizCompletedAt),
      profileTimestampValue(normalizedProfile.createdAt)
    );
  }

  function sortPetProfiles(profiles) {
    return normalizePetProfiles(profiles).sort((first, second) => {
      const freshnessDelta = petProfileFreshness(second) - petProfileFreshness(first);
      if (freshnessDelta !== 0) return freshnessDelta;
      return String(first.petName || first.name || '').localeCompare(String(second.petName || second.name || ''));
    });
  }

  function buildPetProfilesSignature(pets, activePetId) {
    return JSON.stringify({
      activePetId: activePetId || null,
      pets: sortPetProfiles(pets).map((pet) => ({
        id: pet.id,
        updatedAt: pet.updatedAt || '',
        lastQuizCompletedAt: pet.lastQuizCompletedAt || '',
        recommendedProductHandles: arrayify(pet.recommendedProductHandles),
      })),
    });
  }

  function mergePetProfiles(primary, secondary) {
    const merged = new Map();
    [...normalizePetProfiles(primary), ...normalizePetProfiles(secondary)].forEach((profile) => {
      const existing = merged.get(profile.id);
      if (!existing || petProfileFreshness(profile) > petProfileFreshness(existing)) {
        merged.set(profile.id, profile);
      }
    });
    return sortPetProfiles(Array.from(merged.values()));
  }

  function buildCustomerPetsRequestUrl(customerId, intent = 'read') {
    const endpoint = getCustomerPetsEndpoint();
    if (!endpoint) return '';
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('intent', intent);
    url.searchParams.set('v', getCustomerPetsSyncVersion());
    if (customerId) url.searchParams.set('customer_id', String(customerId));
    return url.toString();
  }

  function buildCustomerPetsEnvelope(customerId, pets, activePetId, extra = {}) {
    const storage = getCustomerPetsStorageConfig();
    const locationHref = String(window.location?.href || '').trim();
    const locationPathname = String(window.location?.pathname || '').trim();
    return {
      version: getCustomerPetsSyncVersion(),
      customerId: String(customerId || '').trim(),
      customerEmail: String(extra.customerEmail || currentCustomerEmailFromDocument() || '').trim(),
      activePetId: activePetId || null,
      pets: normalizePetProfiles(pets),
      storage,
      source: {
        app: 'plennia-theme',
        pathname: locationPathname,
        href: locationHref,
        reason: extra.reason || 'sync',
        syncedAt: new Date().toISOString(),
      },
    };
  }

  function normalizeCustomerPetsPayload(payload, fallback = {}) {
    const pets = normalizePetProfiles(payload?.pets || fallback.pets || []);
    const activePetId = String(payload?.activePetId || payload?.active_pet_id || fallback.activePetId || '').trim();

    return {
      synced: Boolean(payload?.synced ?? payload?.ok ?? true),
      customerId: String(payload?.customerId || payload?.customer_id || fallback.customerId || '').trim(),
      pets,
      activePetId: activePetId || null,
      source: String(payload?.source || payload?.storage_source || fallback.source || '').trim(),
      revision: String(payload?.revision || payload?.syncedAt || payload?.updatedAt || '').trim(),
      storage: payload?.storage || fallback.storage || getCustomerPetsStorageConfig(),
    };
  }

  function readCustomerPets(customerId, fallback = []) {
    const keys = getPetStorageKeys(customerId);
    return normalizePetProfiles(readStorage(keys.pets, fallback));
  }

  function writeCustomerPets(customerId, pets, activePetId) {
    const keys = getPetStorageKeys(customerId);
    writeStorage(keys.pets, normalizePetProfiles(pets));
    if (activePetId) {
      writeStorage(keys.activePet, activePetId);
    } else {
      removeStorage(keys.activePet);
    }
  }

  function upsertPetProfile(profiles, profile) {
    const pets = normalizePetProfiles(profiles);
    const nextProfile = normalizePetProfile(profile);
    if (!nextProfile) return pets;

    const existingIndex = pets.findIndex((pet) => pet.id === nextProfile.id);
    if (existingIndex >= 0) pets[existingIndex] = nextProfile;
    else pets.push(nextProfile);

    return pets;
  }

  function persistPendingPetProfile(profile) {
    writeStorage(STORAGE_KEYS.pendingPetProfile, {
      profile: normalizePetProfile(profile),
      createdAt: new Date().toISOString(),
    });
  }

  function consumePendingPetProfile() {
    const pending = readStorage(STORAGE_KEYS.pendingPetProfile, null);
    removeStorage(STORAGE_KEYS.pendingPetProfile);
    return pending && pending.profile ? normalizePetProfile(pending.profile) : null;
  }

  function getInitialPetProfiles(scope = document) {
    return normalizePetProfiles(readJSONScript(scope, '[data-initial-pets]', []));
  }

  function getActivePetProfile(customerId, fallback = []) {
    const resolvedCustomerId = customerId || currentCustomerIdFromDocument() || 'guest';
    const fallbackPets = arrayify(fallback).length ? fallback : getInitialPetProfiles(document);
    const pets = readCustomerPets(resolvedCustomerId, fallbackPets);
    const keys = getPetStorageKeys(resolvedCustomerId);
    const activePetId = readStorage(keys.activePet, getInitialActivePetId(document) || (pets[0] ? pets[0].id : null));
    return pets.find((pet) => pet.id === activePetId) || pets[0] || null;
  }

  function fetchCustomerPets(customerId, fallback = {}) {
    const endpoint = getCustomerPetsEndpoint();
    const fallbackPayload = normalizeCustomerPetsPayload(fallback, {
      customerId,
      activePetId: fallback.activePetId || null,
      pets: fallback.pets || [],
    });

    if (!customerId || !endpoint) {
      return Promise.resolve({ ...fallbackPayload, synced: false, disabled: true });
    }

    const requestUrl = buildCustomerPetsRequestUrl(customerId, 'read');
    return fetch(requestUrl, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || 'Unable to load pet profiles');
        return normalizeCustomerPetsPayload(payload, fallbackPayload);
      })
      .catch((error) => ({ ...fallbackPayload, synced: false, error }));
  }

  function syncCustomerPets(customerId, pets, activePetId, extra = {}) {
    const endpoint = getCustomerPetsEndpoint();
    if (!customerId || !endpoint) {
      return Promise.resolve({
        synced: false,
        disabled: true,
        pets: normalizePetProfiles(pets),
        activePetId: activePetId || null,
        storage: getCustomerPetsStorageConfig(),
      });
    }

    customerPetsHydrationCache.delete(`${customerId}:${endpoint}`);
    const requestUrl = buildCustomerPetsRequestUrl(customerId, 'write');
    return fetch(requestUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(buildCustomerPetsEnvelope(customerId, pets, activePetId, extra)),
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || 'Unable to sync pet profiles');
        return normalizeCustomerPetsPayload(payload, {
          customerId,
          pets,
          activePetId,
        });
      })
      .catch((error) => ({
        synced: false,
        error,
        pets: normalizePetProfiles(pets),
        activePetId: activePetId || null,
        storage: getCustomerPetsStorageConfig(),
      }));
  }

  function hydrateCustomerPets(customerId, fallbackPets = [], options = {}) {
    const resolvedCustomerId = String(customerId || currentCustomerIdFromDocument() || '').trim();
    const endpoint = getCustomerPetsEndpoint();
    const scope = options.scope || document;
    const fallbackActivePetId = options.activePetId || getInitialActivePetId(scope) || getInitialActivePetId(document) || null;
    const localPets = readCustomerPets(resolvedCustomerId, fallbackPets);
    const localActivePetId =
      readStorage(getPetStorageKeys(resolvedCustomerId).activePet, fallbackActivePetId || (localPets[0] ? localPets[0].id : null)) ||
      null;

    if (!resolvedCustomerId || !endpoint) {
      return Promise.resolve({
        hydrated: false,
        disabled: true,
        pets: localPets,
        activePetId: localActivePetId,
      });
    }

    const cacheKey = `${resolvedCustomerId}:${endpoint}`;
    if (!options.force && customerPetsHydrationCache.has(cacheKey)) {
      return customerPetsHydrationCache.get(cacheKey);
    }

    const request = fetchCustomerPets(resolvedCustomerId, {
      customerId: resolvedCustomerId,
      pets: fallbackPets,
      activePetId: fallbackActivePetId,
    })
      .then((remote) => {
        if (!remote.synced) {
          return {
            hydrated: false,
            error: remote.error,
            pets: localPets,
            activePetId: localActivePetId,
          };
        }

        let mergedPets = mergePetProfiles(remote.pets, localPets);
        let nextActivePetId = remote.activePetId || localActivePetId || (mergedPets[0] ? mergedPets[0].id : null);
        const pendingProfile = options.consumePending === false ? null : consumePendingPetProfile();

        if (pendingProfile) {
          mergedPets = upsertPetProfile(mergedPets, pendingProfile);
          nextActivePetId = pendingProfile.id;
        }

        writeCustomerPets(resolvedCustomerId, mergedPets, nextActivePetId);
        document.dispatchEvent(
          new CustomEvent('plennia:pets:updated', { detail: { pets: mergedPets, activePetId: nextActivePetId } })
        );

        if (buildPetProfilesSignature(mergedPets, nextActivePetId) !== buildPetProfilesSignature(remote.pets, remote.activePetId)) {
          return syncCustomerPets(resolvedCustomerId, mergedPets, nextActivePetId, {
            reason: pendingProfile ? 'attach-pending-profile' : 'hydrate-merge',
          }).then((result) => {
            const syncedPets = normalizePetProfiles(result.pets || mergedPets);
            const syncedActivePetId = result.activePetId || nextActivePetId;
            writeCustomerPets(resolvedCustomerId, syncedPets, syncedActivePetId);
            document.dispatchEvent(
              new CustomEvent('plennia:pets:updated', {
                detail: { pets: syncedPets, activePetId: syncedActivePetId },
              })
            );

            return {
              hydrated: true,
              synced: Boolean(result.synced),
              pets: syncedPets,
              activePetId: syncedActivePetId,
              source: result.source || remote.source || 'shopify-customer-metafield',
              storage: result.storage || remote.storage || getCustomerPetsStorageConfig(),
            };
          });
        }

        return {
          hydrated: true,
          synced: true,
          pets: mergedPets,
          activePetId: nextActivePetId,
          source: remote.source || 'shopify-customer-metafield',
          storage: remote.storage || getCustomerPetsStorageConfig(),
        };
      })
      .catch((error) => ({
        hydrated: false,
        error,
        pets: localPets,
        activePetId: localActivePetId,
      }))
      .then((result) => {
        if (!result.hydrated) customerPetsHydrationCache.delete(cacheKey);
        return result;
      });

    customerPetsHydrationCache.set(cacheKey, request);
    return request;
  }

  function buildSavedPlan(state, recommendation) {
    const normalizedState = normalizeQuizState(state);
    const nextRecommendation = recommendation || recommendProducts(getProductsFromScripts(document), normalizedState);
    const copy = buildResultsCopy(normalizedState, nextRecommendation);

    return {
      savedAt: normalizedState.lastQuizCompletedAt || new Date().toISOString(),
      title: copy.title,
      body: copy.body,
      chips: buildSummaryChips(normalizedState),
      messages: nextRecommendation.messages,
      slots: {
        primary: serializeProduct(nextRecommendation.slots.primary),
        secondary: serializeProduct(nextRecommendation.slots.secondary),
        addon: serializeProduct(nextRecommendation.slots.addon),
      },
      groups: arrayify(nextRecommendation.groups).reduce((accumulator, group) => {
        accumulator[group.key] = arrayify(group.products).map((product) => serializeProduct(product));
        return accumulator;
      }, {}),
      futureSlots: arrayify(nextRecommendation.futureSlots).map((slot) => ({ ...slot })),
    };
  }

  function buildPetProfileFromQuiz(state, recommendation, overrides = {}) {
    const normalizedState = normalizeQuizState(state);
    const nextRecommendation = recommendation || recommendProducts(getProductsFromScripts(document), normalizedState);
    const timestamp = normalizedState.lastQuizCompletedAt || new Date().toISOString();
    const recommendedHandles =
      normalizedState.recommendedProductHandles.length > 0
        ? normalizedState.recommendedProductHandles
        : nextRecommendation.recommendedHandles;

    return normalizePetProfile({
      id: overrides.id || normalizedState.petId || `pet-${Date.now()}`,
      pet_name: overrides.petName || normalizedState.petName || '',
      pet_type: overrides.petType || normalizedState.petType || 'dog',
      pet_sex: overrides.petSex || normalizedState.petSex || '',
      pet_age: overrides.petAge || normalizedState.petAge || '',
      pet_age_group: overrides.petAgeGroup || normalizedState.petAgeGroup || '',
      pet_breed: overrides.petBreed || normalizedState.petBreed || '',
      pet_neutered: overrides.petNeutered || normalizedState.petNeutered || '',
      pet_weight: overrides.petWeight || normalizedState.petWeight || '',
      body_condition: overrides.bodyCondition || normalizedState.bodyCondition || '',
      activity_level: overrides.activityLevel || normalizedState.activityLevel || '',
      current_food_types: overrides.currentFoodTypes || normalizedState.currentFoodTypes || [],
      primary_goals: overrides.primaryGoals || normalizedState.primaryGoals || [],
      primary_goal: overrides.primaryGoal || normalizedState.primaryGoal || '',
      health_issues: overrides.healthIssues || normalizedState.healthIssues || [],
      ingredient_allergies: overrides.ingredientAllergies || normalizedState.ingredientAllergies || [],
      recommended_product_handles: recommendedHandles,
      last_quiz_completed_at: timestamp,
      email: overrides.email || normalizedState.email || '',
      savedQuizAnswers: {
        ...normalizedState,
        pet_id: overrides.id || normalizedState.petId || '',
        primary_goals: overrides.primaryGoals || normalizedState.primaryGoals || [],
        recommended_product_handles: recommendedHandles,
        last_quiz_completed_at: timestamp,
      },
      savedRecommendedPlan: buildSavedPlan(
        {
          ...normalizedState,
          recommended_product_handles: recommendedHandles,
          last_quiz_completed_at: timestamp,
        },
        nextRecommendation
      ),
      createdAt: overrides.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  }

  function buildCartItems(recommendation, opts) {
    const options = opts || {};
    const subscribe = !!options.subscribe;
    const items = [];
    const seenVariantIds = new Set();
    const slotProducts = Object.values(recommendation?.slots || {}).filter(Boolean);
    const products =
      slotProducts.length > 0
        ? slotProducts
        : arrayify(recommendation?.groups).flatMap((group) => arrayify(group.products).slice(0, 1));

    products.forEach((product) => {
      const variantId = Number(product?.variant_id || 0);
      if (!variantId || seenVariantIds.has(variantId)) return;
      seenVariantIds.add(variantId);
      const item = { id: variantId, quantity: 1 };
      if (subscribe && product.selling_plan_id) {
        item.selling_plan = product.selling_plan_id;
      }
      items.push(item);
    });
    return items;
  }

  // Build subscription items — only includes products that have a selling plan
  function buildSubscriptionItems(recommendation) {
    const items = buildCartItems(recommendation, { subscribe: true });
    return items.filter((item) => item.selling_plan);
  }

  // Check if any recommended product has a selling plan set
  function hasAnySellingPlan(recommendation) {
    const slotProducts = Object.values(recommendation?.slots || {}).filter(Boolean);
    const products = slotProducts.length > 0
      ? slotProducts
      : arrayify(recommendation?.groups).flatMap((group) => arrayify(group.products));
    return products.some((p) => p && p.selling_plan_id);
  }

  function addItemsToCart(items, coverageDays) {
    if (!arrayify(items).length) return Promise.resolve(null);

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    const endpoint = cartAddUrl.endsWith('.js') ? cartAddUrl : `${cartAddUrl}.js`;

    const enrichedItems = arrayify(items).map((item) => {
      const enriched = { ...item };
      if (coverageDays && coverageDays > 0) {
        enriched.properties = {
          ...(item.properties || {}),
          _plennia_coverage_days: String(Math.round(coverageDays)),
          _plennia_reorder_date: (() => {
            const d = new Date();
            d.setDate(d.getDate() + Math.round(coverageDays) - 5);
            return d.toISOString().split('T')[0];
          })(),
        };
      }
      return enriched;
    });

    const payload = { items: enrichedItems };

    if (cart && typeof cart.getSectionsToRender === 'function') {
      payload.sections = cart.getSectionsToRender().map((section) => section.id);
      payload.sections_url = window.location.pathname;
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
    }

    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then((response) => {
      if (!response.ok) throw new Error('Unable to add items');
      return response.json().then((parsedState) => {
        if (parsedState.status) throw new Error(parsedState.description || 'Unable to add items');

        const publishPromise =
          typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
            ? publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'plennia-plan-results',
                productVariantId: arrayify(items)[0]?.id,
                cartData: parsedState,
              })
            : Promise.resolve();

        return Promise.resolve(publishPromise)
          .catch(() => null)
          .then(() => {
            if (cart && typeof cart.renderContents === 'function') {
              cart.renderContents(parsedState);
            }

            return parsedState;
          });
      });
    });
  }

  function productPetLabel(product) {
    if (hasTag(product, PLAN_CONFIG.tags.species.cat)) return 'For cats';
    if (hasTag(product, PLAN_CONFIG.tags.species.dog)) return 'For dogs';
    return 'For pets';
  }

  function formatLabel(format) {
    if (format === 'dry') return 'Dry food';
    if (format === 'wet') return 'Wet food';
    if (format === 'treat') return 'Treat';
    return 'Product';
  }

  function buildProductReasonText(product) {
    const reasons = arrayify(product?.reasons).filter(Boolean);
    if (reasons.length) return `Why it fits: ${readableList(reasons).toLowerCase()}.`;
    if (product?.description) return product.description;
    return `Closest ${productPetLabel(product).replace(/^For /, '').toLowerCase()} ${formatLabel(product?.format).toLowerCase()} match in the current catalog.`;
  }

  function renderProductCardMarkup(product, currencyCode, opts) {
    if (!product) return '';
    const options = opts || {};
    const cardId = `plennia-card-${product.variant_id || product.id || Math.random().toString(36).slice(2, 9)}`;
    const comparePrice = Number(product.compare_at_price || 0);
    const basePrice = Number(product.price || 0);
    const hasCompare = comparePrice > basePrice;
    const imageMarkup = product.featured_image
      ? `<img src="${product.featured_image}" alt="${product.title}" loading="lazy">`
      : '<div class="plennia-plan-product-card__placeholder"></div>';

    const sellingPlanId = product.selling_plan_id || options.defaultSellingPlanId || null;
    const discountPct = Number(product.subscription_discount || options.subscriptionDiscount || DEFAULT_SUBSCRIPTION_DISCOUNT);
    const hasSubscription = !!sellingPlanId;
    const subPrice = hasSubscription ? Math.round(basePrice * (1 - discountPct / 100)) : 0;

    // --- Reasons / tags ---
    const reasonsMarkup = arrayify(product.reasons).length
      ? `<div class="plennia-plan-results-product-card__reasons">${arrayify(product.reasons)
          .map((r) => `<span class="plennia-plan-results-product-card__reason">${r}</span>`)
          .join('')}</div>`
      : '';

    // --- Purchase type toggle ---
    const toggleMarkup = hasSubscription
      ? `<div class="plennia-plan-product-card__purchase-type" data-purchase-toggle="${cardId}">
          <label class="plennia-plan-product-card__type-option plennia-plan-product-card__type-option--active">
            <input type="radio" name="purchase-type-${cardId}" value="once" checked>
            <span class="plennia-plan-product-card__type-label">One-time</span>
          </label>
          <label class="plennia-plan-product-card__type-option">
            <input type="radio" name="purchase-type-${cardId}" value="subscribe">
            <span class="plennia-plan-product-card__type-label">
              Subscribe
              <em class="plennia-plan-product-card__save-pill">Save ${discountPct}%</em>
            </span>
          </label>
        </div>`
      : `<div class="plennia-plan-product-card__sub-teaser">
          <svg class="plennia-plan-product-card__sub-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.5 2v6h-6"/><path d="M2.5 22v-6h6"/><path d="M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/></svg>
          Subscribe &amp; Save — coming soon
        </div>`;

    // --- Price block ---
    const oncePriceMarkup = `<div class="plennia-plan-product-card__price" data-price-once="${cardId}">
        ${hasCompare ? `<s class="plennia-plan-product-card__compare">${formatMoney(comparePrice, currencyCode)}</s>` : ''}
        <span>${formatMoney(basePrice, currencyCode)}</span>
      </div>`;

    const subPriceMarkup = hasSubscription
      ? `<div class="plennia-plan-product-card__price plennia-plan-product-card__price--sub" data-price-subscribe="${cardId}" hidden>
          <s class="plennia-plan-product-card__compare">${formatMoney(basePrice, currencyCode)}</s>
          <span class="plennia-plan-product-card__sub-price">${formatMoney(subPrice, currencyCode)}</span>
          <span class="plennia-plan-product-card__save-badge">–${discountPct}%</span>
        </div>`
      : '';

    // --- Action buttons ---
    const canAdd = product.variant_id && product.available !== false;
    const addOnceBtn = canAdd
      ? `<button class="button plennia-plan-product-card__btn-once" type="button"
            data-plennia-add-item
            data-variant-id="${product.variant_id}"
            data-card-id="${cardId}">Add to cart</button>`
      : '';

    const subscribeBtn = hasSubscription && canAdd
      ? `<button class="button plennia-plan-product-card__btn-subscribe" type="button"
            data-plennia-subscribe-item
            data-variant-id="${product.variant_id}"
            data-selling-plan-id="${sellingPlanId}"
            data-card-id="${cardId}"
            hidden>Subscribe &amp; Save</button>`
      : '';

    return `
      <article class="plennia-plan-product-card plennia-plan-results-product-card" data-card-id="${cardId}">
        <div class="plennia-plan-product-card__tile">${imageMarkup}</div>
        <div class="plennia-plan-product-card__body">
          <p class="plennia-plan-product-card__meta">${productPetLabel(product)} <span>&bull;</span> ${formatLabel(product.format)}</p>
          <h3 class="plennia-plan-product-card__title">${product.title}</h3>
          <p class="plennia-plan-product-card__excerpt">${buildProductReasonText(product)}</p>
          ${reasonsMarkup}
          ${toggleMarkup}
          ${oncePriceMarkup}
          ${subPriceMarkup}
          <div class="plennia-plan-product-card__actions">
            ${addOnceBtn}
            ${subscribeBtn}
            <a class="button button--secondary plennia-button-secondary" href="${product.url}">View product</a>
          </div>
        </div>
      </article>
    `;
  }

  function computeFeedingGuide(state) {
    const weight = parseFloat(state.petWeight || state.weight) || 0;
    if (!weight || weight <= 0) return null;
    const species = (state.petType || state.species || 'dog').toLowerCase();
    const activityScore = parseInt(state.activityScore, 10) || 3;
    const activity = activityScore <= 2 ? 'low' : activityScore >= 4 ? 'active' : 'normal';
    const bodyCondition = (state.bodyCondition || 'ideal').toLowerCase();
    const goal = bodyCondition === 'overweight' ? 'lose' : bodyCondition === 'underweight' ? 'gain' : 'maintain';
    const multipliers = {
      dog: { low: 1.2, normal: 1.6, active: 2.0, lose: 1.0, gain: 1.8 },
      cat: { low: 1.2, normal: 1.4, active: 1.6, lose: 1.0, gain: 1.6 },
    };
    const map = multipliers[species] || multipliers.dog;
    const multiplier = goal === 'lose' ? map.lose : goal === 'gain' ? map.gain : (map[activity] || map.normal);
    const dailyKcal = Math.round(70 * Math.pow(weight, 0.75) * multiplier);
    const currentFoods = arrayify(state.currentFoodTypes || state.current_food_types);
    const eatsDry = currentFoods.includes('dry') || currentFoods.length === 0;
    const eatsWet = currentFoods.includes('wet');
    let dryRatio = 0, wetRatio = 0;
    if (eatsDry && eatsWet) { dryRatio = 0.7; wetRatio = 0.3; }
    else if (eatsWet) { dryRatio = 0; wetRatio = 1; }
    else { dryRatio = 1; wetRatio = 0; }
    const DRY_KCAL_PER_G = 3.6;
    const WET_KCAL_PER_POUCH = 120;
    const WET_POUCH_G = 85;
    const dryGrams = dryRatio > 0 ? Math.round((dailyKcal * dryRatio) / DRY_KCAL_PER_G) : 0;
    const wetPouches = wetRatio > 0 ? Math.max(1, Math.ceil((dailyKcal * wetRatio) / WET_KCAL_PER_POUCH)) : 0;
    const monthlyCoverage = Math.round(30);
    const monthlyDryKg = dryGrams > 0 ? parseFloat(((dryGrams * 30) / 1000).toFixed(2)) : 0;
    const monthlyWetPouches = wetPouches * 30;
    return { dailyKcal, dryGrams, wetPouches, dryRatio, wetRatio, monthlyDryKg, monthlyWetPouches, monthlyCoverage, goal, activity };
  }

  function renderFeedingGuideMarkup(state) {
    const guide = computeFeedingGuide(state);
    if (!guide) return '';
    const petName = state.petName || 'your pet';
    const dryHalf = guide.dryGrams > 0 ? `${Math.round(guide.dryGrams / 2)}g dry` : '';
    const wetHalf = guide.wetPouches > 0 ? `½ pouch wet` : '';
    const perMealParts = [dryHalf, wetHalf].filter(Boolean).join(' + ');
    const monthlyNote = [
      guide.monthlyDryKg > 0 ? `${guide.monthlyDryKg}kg dry` : '',
      guide.monthlyWetPouches > 0 ? `${guide.monthlyWetPouches} wet pouches` : '',
    ].filter(Boolean).join(' + ');
    return `
      <div class="plennia-plan-results-products__feeding-guide">
        <div class="plennia-plan-results-products__feeding-head">
          <p class="plennia-section__eyebrow">Daily feeding guide</p>
          <h3 class="plennia-card__title">${petName.charAt(0).toUpperCase() + petName.slice(1)}'s daily portion</h3>
          <p class="plennia-section__body">Based on weight, activity and body condition. Figures will update once final recipe COAs are confirmed.</p>
        </div>
        <div class="plennia-plan-results-products__feeding-stats">
          <div class="plennia-plan-results-products__feeding-stat">
            <span class="plennia-plan-results-products__feeding-value">${guide.dailyKcal}</span>
            <span class="plennia-plan-results-products__feeding-label">kcal / day</span>
          </div>
          ${guide.dryGrams > 0 ? `
          <div class="plennia-plan-results-products__feeding-stat">
            <span class="plennia-plan-results-products__feeding-value">${guide.dryGrams}g</span>
            <span class="plennia-plan-results-products__feeding-label">dry food / day</span>
          </div>` : ''}
          ${guide.wetPouches > 0 ? `
          <div class="plennia-plan-results-products__feeding-stat">
            <span class="plennia-plan-results-products__feeding-value">${guide.wetPouches}</span>
            <span class="plennia-plan-results-products__feeding-label">wet pouch${guide.wetPouches > 1 ? 'es' : ''} / day</span>
          </div>` : ''}
          <div class="plennia-plan-results-products__feeding-stat">
            <span class="plennia-plan-results-products__feeding-value">2×</span>
            <span class="plennia-plan-results-products__feeding-label">meals / day</span>
          </div>
        </div>
        <p class="plennia-plan-results-products__feeding-note">
          Split into 2 meals${perMealParts ? ': ' + perMealParts + ' each' : ''}. Monthly you'll need approx. ${monthlyNote}.
        </p>
      </div>
    `;
  }


  function renderSubscriptionBanner(recommendation, currencyCode, state) {
    if (!recommendation || !recommendation.hasResults) return '';
    const petName = state?.petName || 'your pet';
    const petNameCap = petName.charAt(0).toUpperCase() + petName.slice(1);
    const allItems = buildCartItems(recommendation, { subscribe: true });
    const subItems = allItems.filter((item) => item.selling_plan);
    const subscriptionsAvailable = subItems.length > 0;
    const discount = DEFAULT_SUBSCRIPTION_DISCOUNT;

    const ctaMarkup = subscriptionsAvailable
      ? `<button class="button plennia-plan-results-sub__cta"
            type="button"
            data-recommendation-subscribe-all
            data-items='${JSON.stringify(subItems)}'>
            Subscribe &amp; Save ${discount}% — deliver monthly
          </button>`
      : `<p class="plennia-plan-results-sub__coming">
            <span class="plennia-badge plennia-badge--muted">Coming soon</span>
            Subscriptions are on the way — your plan is saved and ready.
          </p>`;

    return `
      <section class="plennia-plan-results-sub">
        <div class="plennia-plan-results-sub__inner">
          <div class="plennia-plan-results-sub__eyebrow">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.5 2v6h-6"/><path d="M2.5 22v-6h6"/><path d="M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/></svg>
            Plennia Plan
          </div>
          <h3 class="plennia-plan-results-sub__title">Never run out for ${petNameCap}</h3>
          <p class="plennia-plan-results-sub__body">
            Subscribe to your recommended plan and save ${discount}% on every delivery,
            automatically timed to your pet's monthly needs.
          </p>
          <ul class="plennia-plan-results-sub__perks">
            <li><span class="plennia-plan-results-sub__perk-check">✓</span> ${discount}% off every order</li>
            <li><span class="plennia-plan-results-sub__perk-check">✓</span> Free delivery</li>
            <li><span class="plennia-plan-results-sub__perk-check">✓</span> Pause or cancel anytime</li>
            <li><span class="plennia-plan-results-sub__perk-check">✓</span> Adjust quantities each month</li>
          </ul>
          ${ctaMarkup}
        </div>
      </section>
    `;
  }

  function renderSlotsMarkup(recommendation, currencyCode, state) {
    const slotOrder = ['primary', 'secondary', 'addon', 'booster'];
    const messagesMarkup = arrayify(recommendation?.messages).length
      ? `<div class="plennia-plan-results-products__messages">${arrayify(recommendation.messages)
          .map((message) => `<p class="plennia-plan-results-products__message">${message}</p>`)
          .join('')}</div>`
      : '';
    const feedingGuide = state ? renderFeedingGuideMarkup(state) : '';
    const slotsMarkup = slotOrder
      .map((slotKey) => {
        const slotMeta = PLAN_SLOT_META[slotKey];
        if (!slotMeta) return '';
        const product = recommendation?.slots?.[slotKey];
        if (slotMeta.comingSoon) {
          return `
            <section class="plennia-plan-results-products__slot plennia-plan-results-products__slot--coming-soon">
              <div class="plennia-plan-results-products__slot-copy">
                <strong>${slotMeta.label}</strong>
                <h3>${slotMeta.heading}</h3>
                <p>${slotMeta.body}</p>
              </div>
              <div class="plennia-plan-results-products__slot-grid">
                <article class="plennia-plan-results-products__coming-soon-card">
                  <p class="plennia-badge plennia-badge--muted">Coming soon</p>
                  <p class="plennia-card__excerpt">Plennia Boost toppers will be matched to your pet's primary goal automatically once the range launches.</p>
                </article>
              </div>
            </section>
          `;
        }
        if (!product) return '';
        return `
          <section class="plennia-plan-results-products__slot">
            <div class="plennia-plan-results-products__slot-copy">
              <strong>${slotMeta.label}</strong>
              <h3>${slotMeta.heading}</h3>
              <p>${slotMeta.body}</p>
            </div>
            <div class="plennia-plan-results-products__slot-grid">
              ${renderProductCardMarkup(product, currencyCode)}
            </div>
          </section>
        `;
      })
      .filter(Boolean)
      .join('');

    const subscriptionBanner = renderSubscriptionBanner(recommendation, currencyCode, state);
    return `${messagesMarkup}${feedingGuide}<div class="plennia-plan-results-products__slots">${slotsMarkup}</div>${subscriptionBanner}`;
  }

  function renderRecommendationGroupsMarkup(recommendation, currencyCode) {
    const messagesMarkup = arrayify(recommendation?.messages).length
      ? `<div class="plennia-plan-results-products__messages">${arrayify(recommendation.messages)
          .map((message) => `<p class="plennia-plan-results-products__message">${message}</p>`)
          .join('')}</div>`
      : '';
    const groupsMarkup = arrayify(recommendation?.groups)
      .map((group) => {
        const productsMarkup = arrayify(group.products).length
          ? `<div class="plennia-plan-results-products__grid">${arrayify(group.products)
              .map((product) => renderProductCardMarkup(product, currencyCode))
              .join('')}</div>`
          : `<div class="plennia-plan-results-products__empty"><p>${group.empty}</p></div>`;

        return `
          <section class="plennia-plan-results-products__group">
            <div class="plennia-plan-results-products__group-head">
              <p>${group.eyebrow}</p>
              <h3>${group.heading}</h3>
            </div>
            ${productsMarkup}
          </section>
        `;
      })
      .join('');
    const futureMarkup = arrayify(recommendation?.futureSlots).length
      ? `
        <section class="plennia-plan-results-products__group plennia-plan-results-products__group--future">
          <div class="plennia-plan-results-products__group-head">
            <p>Future plan slots</p>
            <h3>Ready for the next recommendation layers</h3>
          </div>
          <div class="plennia-plan-results-products__future-grid">
            ${arrayify(recommendation.futureSlots)
              .map(
                (slot) => `
                  <article class="plennia-plan-results-products__future-card">
                    <h4>${slot.heading}</h4>
                    <p>${slot.body}</p>
                  </article>
                `
              )
              .join('')}
          </div>
        </section>
      `
      : '';

    return `${messagesMarkup}${groupsMarkup}${futureMarkup}`;
  }

  function renderSavedPlanMarkup(plan) {
    if (!plan) {
      return '<p class="plennia-note">No saved plan yet. Complete Create a Plan to save a recommendation set for this pet.</p>';
    }

    const rows = [
      ['Primary recommendation', plan.slots?.primary?.title],
      ['Secondary recommendation', plan.slots?.secondary?.title],
      ['Functional add-on', plan.slots?.addon?.title],
    ]
      .filter(([, value]) => value)
      .map(
        ([label, value]) => `
          <div class="plennia-note-list__item">
            <span>${label}</span>
            <span>${value}</span>
          </div>
        `
      )
      .join('');

    return `
      <div class="plennia-card__copy">
        <h3 class="plennia-card__title">${plan.title || 'Saved plan'}</h3>
        ${plan.body ? `<p class="plennia-card__excerpt">${plan.body}</p>` : ''}
      </div>
      <div class="plennia-saved-plan">
        ${arrayify(plan.chips)
          .map((chip) => `<span class="plennia-badge plennia-badge--muted">${chip}</span>`)
          .join('')}
      </div>
      ${rows ? `<div class="plennia-note-list plennia-saved-plan__list">${rows}</div>` : ''}
      ${arrayify(plan.messages)
        .map((message) => `<p class="plennia-note">${message}</p>`)
        .join('')}
    `;
  }

  function parseCommaList(value) {
    return normalizeChoiceList(String(value || '').split(',').map((item) => item.trim()));
  }

  function listToInputValue(items) {
    return arrayify(items)
      .map((item) => String(item).replace(/_/g, ' '))
      .join(', ');
  }

  function petHasSavedPlan(profile) {
    const pet = normalizePetProfile(profile);
    if (!pet) return false;
    const slots = pet.savedRecommendedPlan && pet.savedRecommendedPlan.slots ? Object.values(pet.savedRecommendedPlan.slots) : [];
    return Boolean(pet.lastQuizCompletedAt || arrayify(pet.recommendedProductHandles).length || slots.some(Boolean));
  }

  function petHasSavedQuiz(profile) {
    const pet = normalizePetProfile(profile);
    if (!pet) return false;
    return Boolean(
      pet.lastQuizCompletedAt ||
        pet.bodyCondition ||
        pet.activityLevel ||
        arrayify(pet.primaryGoals).length ||
        arrayify(pet.currentFoodTypes).length ||
        arrayify(pet.healthIssues).length ||
        arrayify(pet.ingredientAllergies).length
    );
  }

  class PlenniaQuiz extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      document.body.classList.add('plennia-has-plan-quiz');
      this.storageKey = this.dataset.storageKey || STORAGE_KEYS.quiz;
      this.currencyCode = this.dataset.currency || 'USD';
      this.resultsUrl = this.dataset.resultsUrl || '';
      this.homeUrl = this.dataset.homeUrl || '/';
      this.customerId = this.dataset.customerId || currentCustomerIdFromDocument() || '';
      this.customerEmail = this.dataset.customerEmail || '';
      this.dogIconUrl = this.dataset.dogIconUrl || '';
      this.catIconUrl = this.dataset.catIconUrl || '';
      this.collectionHandle = this.dataset.sourceCollectionHandle || 'all';
      this.form = this.querySelector('form');
      this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
      this.progressLabel = this.querySelector('[data-quiz-progress-label]');
      this.progressBar = this.querySelector('[data-quiz-progress-bar]');
      this.prevButton = this.querySelector('[data-quiz-prev]');
      this.nextButton = this.querySelector('[data-quiz-next]');
      this.errorTarget = this.querySelector('[data-quiz-error]');
      this.basicIntroTarget = this.querySelector('[data-plan-basic-intro]');
      this.petImageTargets = Array.from(this.querySelectorAll('[data-plan-pet-image]'));
      this.fieldTargets = new Map(
        Array.from(this.querySelectorAll('[data-quiz-field]')).map((node) => [node.dataset.quizField, node])
      );
      this.fieldErrorTargets = new Map(
        Array.from(this.querySelectorAll('[data-field-error-for]')).map((node) => [node.dataset.fieldErrorFor, node])
      );
      this.activityInput = this.form?.querySelector('[name="activity_score"]');
      this.emailInput = this.form?.querySelector('[name="email"]');
      this.emailBody = this.querySelector('[data-quiz-email-body]');
      this.authCopy = this.querySelector('[data-quiz-auth-copy]');
      this.products = normalizePlanProductList(getProductsFromScripts(this));
      this.state = getQuizState(this.storageKey);
      this.currentStepIndex = 0;
      this.isLongForm = false;
      this.hasSelectedPetContext = false;
      this.devStepOverride = this.getDevStepOverride();

      this.prepareProfileContext();
      this.bindEvents();
      this.hydrateForm();
      this.syncDynamicCopy();
      this.updateActivityUI();
      if (this.devStepOverride === 'results' && this.resultsUrl) {
        window.location.href = this.resultsUrl;
        return;
      }
      this.goToStep(this.getInitialStepIndex());
      this.loadProductCatalog();

      if (this.customerId) {
        hydrateCustomerPets(this.customerId, [], { scope: this }).then((result) => {
          if (!result.hydrated) return;
          if (this.form) this.form.reset();
          this.prepareProfileContext();
          this.state = getQuizState(this.storageKey);
          this.hydrateForm();
          this.syncDynamicCopy();
          this.updateActivityUI();
          if (this.devStepOverride === 'results' && this.resultsUrl) {
            window.location.href = this.resultsUrl;
            return;
          }
          this.goToStep(this.getInitialStepIndex());
        });
      }
    }

    disconnectedCallback() {
      document.body.classList.remove('plennia-has-plan-quiz');
    }

    prepareProfileContext() {
      const currentUrl = new URL(window.location.href);
      const pets = readCustomerPets(this.customerId, []);
      const context = getQuizContext();
      let petId = currentUrl.searchParams.get('pet');
      this.hasSelectedPetContext = false;

      if (!petId && context.petProfileId) petId = context.petProfileId;

      if (!petId) {
        clearQuizContext();
        this.state = setQuizState({ email: this.customerId ? this.customerEmail : '' }, this.storageKey);
        return;
      }

      const pet = pets.find((profile) => profile.id === petId);
      if (!pet) {
        clearQuizContext();
        this.state = setQuizState({ email: this.customerId ? this.customerEmail : '' }, this.storageKey);
        return;
      }

      this.hasSelectedPetContext = true;
      this.state = buildQuizStateFromPetProfile(pet);
      setQuizContext({ petProfileId: pet.id, customerId: this.customerId || '' });
      setQuizState(this.state, this.storageKey);
    }

    loadProductCatalog() {
      fetchCollectionProducts(this.collectionHandle)
        .then((products) => {
          const normalizedProducts = normalizePlanProductList(products);
          if (!normalizedProducts.length) return;
          this.products = normalizedProducts;
        })
        .catch(() => {
          return;
        });
    }

    bindEvents() {
      if (!this.form) return;

      this.addEventListener('click', (event) => {
        const nextButton = event.target.closest('[data-quiz-next]');
        const prevButton = event.target.closest('[data-quiz-prev]');
        const activityButton = event.target.closest('[data-activity-change]');

        if (activityButton) {
          event.preventDefault();
          this.changeActivityScore(Number(activityButton.dataset.activityChange || 0));
        }

        if (prevButton) {
          event.preventDefault();
          this.captureState();
          this.clearFieldErrors();
          this.clearError();
          if (this.currentStepIndex === 0) {
            window.location.href = this.homeUrl;
            return;
          }

          this.goToStep(this.isLongForm ? 0 : this.getPreviousStepIndex());
          return;
        }

        if (nextButton) {
          event.preventDefault();
          this.captureState();
          if (this.currentStepIndex === 0) {
            if (!this.validateStep(0)) return;
            const nextIncompleteStep = Math.max(1, this.findFirstIncompleteStep());
            this.goToStep(nextIncompleteStep);
            return;
          }

          if (!this.validateLongForm()) return;
          this.finalizeQuiz();
        }
      });

      this.form.addEventListener('change', (event) => {
        const input = event.target;
        if (input && input.matches('input[name="pet_type"]')) {
          this.hasSelectedPetContext = false;
          this.state = setQuizState(
            {
              pet_type: input.value,
              email: this.customerId ? this.customerEmail : '',
            },
            this.storageKey
          );
          clearQuizContext();
          this.syncDynamicCopy();
          this.clearFieldErrors();
          this.clearError();
          this.updatePrimaryActionState();
          return;
        }

        if (input && input.matches('input[type="checkbox"][data-exclusive-none]')) {
          this.toggleExclusiveNone(input);
        } else if (input && input.matches('input[type="checkbox"]:not([data-exclusive-none])')) {
          this.clearNoneForGroup(input.name);
        }

        this.captureState();
        this.syncDynamicCopy();
        this.clearFieldErrors();
        this.clearError();
        this.updatePrimaryActionState();
      });

      this.form.addEventListener('input', () => {
        this.captureState();
        this.syncDynamicCopy();
        this.clearFieldErrors();
        this.clearError();
        this.updatePrimaryActionState();
      });

      this.querySelectorAll('[data-plan-body-icon]').forEach((node) => {
        node.addEventListener('error', () => {
          const fallbackIcon = node.closest('.plennia-plan-option__icon')?.querySelector('[data-plan-body-fallback-icon]');
          node.dataset.imageMissing = 'true';
          node.hidden = true;
          if (fallbackIcon) fallbackIcon.hidden = false;
        });
      });
    }

    toggleExclusiveNone(input) {
      if (!input.checked) return;
      const group = this.form.querySelectorAll(`input[name="${input.name}"]`);
      group.forEach((field) => {
        if (field !== input) field.checked = false;
      });
    }

    clearNoneForGroup(name) {
      const noneField = this.form.querySelector(`input[name="${name}"][value="none"]`);
      if (noneField) noneField.checked = false;
    }

    changeActivityScore(delta) {
      const currentValue = Number(this.activityInput?.value || 3);
      const nextValue = Math.max(1, Math.min(5, currentValue + delta));
      if (this.activityInput) this.activityInput.value = String(nextValue);
      this.captureState();
      this.updateActivityUI();
      this.syncDynamicCopy();
    }

    hydrateForm() {
      if (!this.form) return;

      Object.entries(this.state).forEach(([key, value]) => {
        const fields = Array.from(this.form.querySelectorAll(`[name="${key}"]`));
        if (!fields.length) return;

        if (Array.isArray(value)) {
          value.forEach((item) => {
            const field = this.form.querySelector(`[name="${key}"][value="${item}"]`);
            if (field) field.checked = true;
          });
          return;
        }

        const field = fields[0];
        if (field.matches('input[type="radio"]')) {
          fields.forEach((radio) => {
            radio.checked = radio.value === String(value || '');
          });
          return;
        }

        if (!field.matches('input[type="checkbox"]')) field.value = value;
      });

      if (this.emailInput && this.customerEmail && !this.emailInput.value) this.emailInput.value = this.customerEmail;
      if (this.activityInput && !this.activityInput.value) {
        this.activityInput.value = String(this.state.activityScore || activityScoreForLevel(this.state.activityLevel));
      }
    }

    captureState() {
      const data = new FormData(this.form);
      const petType = data.get('pet_type') || this.state.petType || '';
      const petAge = data.get('age') || this.state.petAge || '';
      const activityScore = Number(data.get('activity_score') || 3);
      const activityStep = activityStepForScore(activityScore);
      const nextState = normalizeQuizState({
        pet_id: data.get('pet_id') || this.state.petId || '',
        pet_type: petType,
        pet_name: data.get('pet_name'),
        pet_sex: data.get('sex'),
        pet_age: petAge,
        pet_breed: data.get('breed'),
        pet_neutered: data.get('pet_neutered'),
        pet_weight: data.get('pet_weight'),
        pet_age_group: mapAgeGroup(petType, petAge),
        body_condition: data.get('body_condition'),
        activity_level: activityStep.level,
        activity_score: activityScore,
        current_food_types: data.getAll('current_food_types'),
        primary_goals: data.getAll('primary_goal'),
        health_issues: data.getAll('health_issues'),
        ingredient_allergies: data.getAll('ingredient_allergies'),
        email: data.get('email') || this.customerEmail || this.state.email || '',
        recommended_product_handles: this.state.recommendedProductHandles || [],
        last_quiz_completed_at: this.state.lastQuizCompletedAt || '',
      });

      this.state = setQuizState(nextState, this.storageKey);
      const ageGroupField = this.form.querySelector('[name="pet_age_group"]');
      if (ageGroupField) ageGroupField.value = this.state.petAgeGroup || '';
      return this.state;
    }

    getStepSequence() {
      return this.customerId ? [0, 1, 2, 3, 4] : [0, 1, 2, 3, 4, 5];
    }

    getDevStepOverride() {
      let rawStep = '';

      try {
        rawStep = new URL(window.location.href).searchParams.get('step') || '';
      } catch (error) {
        rawStep = '';
      }

      const normalizedStep = normalizeToken(rawStep);
      if (!normalizedStep) return null;
      if (normalizedStep === 'results') return 'results';

      const stepNumber = Number.parseInt(normalizedStep, 10);
      if (!Number.isFinite(stepNumber) || stepNumber < 1) return null;

      const sequence = this.getStepSequence();
      return sequence[stepNumber - 1] ?? null;
    }

    getInitialStepIndex() {
      if (Number.isInteger(this.devStepOverride)) return this.devStepOverride;
      if (this.hasSelectedPetContext) return Math.max(1, this.findFirstIncompleteStep());
      return 0;
    }

    getStepPosition(stepIndex) {
      return Math.max(0, this.getStepSequence().indexOf(stepIndex));
    }

    getLastStepIndex() {
      const sequence = this.getStepSequence();
      return sequence[sequence.length - 1];
    }

    getNextStepIndex() {
      const sequence = this.getStepSequence();
      const currentPosition = this.getStepPosition(this.currentStepIndex);
      return sequence[Math.min(currentPosition + 1, sequence.length - 1)];
    }

    getPreviousStepIndex() {
      const sequence = this.getStepSequence();
      const currentPosition = this.getStepPosition(this.currentStepIndex);
      return sequence[Math.max(currentPosition - 1, 0)];
    }

    findFirstIncompleteStep() {
      const state = this.state;
      if (!state.petType) return 0;
      if (!state.petName || !state.petSex || !state.petAge || !state.petBreed || !state.petNeutered || !state.petWeight)
        return 1;
      if (!state.bodyCondition || !state.activityLevel || !state.currentFoodTypes.length) return 2;
      if (!state.primaryGoals.length) return 3;
      if (!state.healthIssues.length || !state.ingredientAllergies.length) return 4;
      if (!this.customerId && !state.email) return 5;
      return this.getLastStepIndex();
    }

    getStepValidation(index, state = this.state) {
      const step = this.steps[index];
      const stepKey = step ? step.dataset.stepKey : '';
      const fieldMessages = [];
      let message = '';

      if (stepKey === 'pet_type' && !state.petType) {
        message = 'Choose whether you are building a plan for a dog or a cat.';
      }

      if (stepKey === 'basic_info') {
        [
          ['pet_name', state.petName, "Please enter your pet's name"],
          ['sex', state.petSex, 'Please select male or female'],
          ['age', state.petAge, 'Please enter age'],
          ['breed', state.petBreed, 'Please enter breed'],
          ['pet_neutered', state.petNeutered, 'Please select yes or no for neutered'],
          ['pet_weight', state.petWeight, 'Please enter weight'],
        ].forEach(([name, value, fieldMessage]) => {
          if (!value) fieldMessages.push({ name, message: fieldMessage });
        });
      }

      if (stepKey === 'body_activity_food' && (!state.bodyCondition || !state.activityLevel || !state.currentFoodTypes.length)) {
        message = 'Select body condition, activity level, and at least one current food type.';
      }

      if (stepKey === 'primary_goal' && !state.primaryGoals.length) {
        message = 'Choose at least one objective you want Plennia to focus on.';
      }

      if (stepKey === 'health_allergies' && (!state.healthIssues.length || !state.ingredientAllergies.length)) {
        message = 'Select at least one health status and one ingredient allergy option, even if it is none.';
      }

      if (stepKey === 'email' && !this.customerId) {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!state.email || !emailPattern.test(state.email)) {
          message = 'Enter a valid email address so this guest plan can be attached later.';
        }
      }

      return {
        index,
        step,
        stepKey,
        message,
        fieldMessages,
        valid: !message && !fieldMessages.length,
      };
    }

    validateStep(index) {
      const result = this.getStepValidation(index, this.captureState());
      this.clearFieldErrors();
      this.clearError();

      if (result.fieldMessages.length) {
        result.fieldMessages.forEach((field) => this.showFieldError(field.name, field.message));
        return false;
      }

      if (result.message) {
        this.showError(result.message);
        return false;
      }

      return true;
    }

    validateLongForm() {
      const state = this.captureState();
      const longFormSequence = this.getStepSequence().filter((stepIndex) => stepIndex > 0);
      let firstInvalid = null;

      this.clearFieldErrors();
      this.clearError();

      for (const index of longFormSequence) {
        const result = this.getStepValidation(index, state);
        if (result.valid) continue;

        if (!firstInvalid) firstInvalid = result;
        result.fieldMessages.forEach((field) => this.showFieldError(field.name, field.message));
      }

      if (!firstInvalid) return true;

      if (firstInvalid.message) this.showError(firstInvalid.message);
      this.goToStep(firstInvalid.index);

      window.requestAnimationFrame(() => {
        const focusTargetName = firstInvalid.fieldMessages[0]?.name;
        const focusTarget = focusTargetName
          ? this.form?.querySelector(`[name="${focusTargetName}"]`)
          : firstInvalid.step?.querySelector('input, select, textarea, button');

        if (focusTarget && typeof focusTarget.focus === 'function') {
          focusTarget.focus({ preventScroll: true });
        }
      });

      return false;
    }

    showError(message) {
      if (!this.errorTarget) return;
      if (!message) {
        this.clearError();
        return;
      }
      this.errorTarget.hidden = false;
      this.errorTarget.textContent = message;
    }

    clearError() {
      if (!this.errorTarget) return;
      this.errorTarget.hidden = true;
      this.errorTarget.textContent = '';
    }

    clearFieldErrors() {
      this.fieldTargets.forEach((field) => field.classList.remove('is-invalid'));
      this.fieldErrorTargets.forEach((target) => {
        target.hidden = true;
        target.textContent = '';
      });
    }

    showFieldError(name, message) {
      const fieldTarget = this.fieldTargets.get(name);
      const errorTarget = this.fieldErrorTargets.get(name);
      if (fieldTarget) fieldTarget.classList.add('is-invalid');
      if (!errorTarget) return;
      errorTarget.hidden = false;
      errorTarget.textContent = message;
    }

    syncDynamicCopy() {
      const petTypeLabel = this.state.petType === 'cat' ? 'cat' : this.state.petType === 'dog' ? 'dog' : 'pet';
      const hasPetName = Boolean(String(this.state.petName || '').trim());
      const petName = hasPetName ? this.state.petName : `your ${petTypeLabel}`;
      const petNamePossessive = hasPetName
        ? petName.endsWith('s')
          ? `${petName}'`
          : `${petName}'s`
        : `your ${petTypeLabel}'s`;
      const pronouns = pronounsForState(this.state);

      this.querySelectorAll('[data-plan-pet-name]').forEach((node) => {
        node.textContent = petName;
      });
      this.querySelectorAll('[data-plan-pet-name-possessive]').forEach((node) => {
        node.textContent = petNamePossessive;
      });
      this.querySelectorAll('[data-plan-pet-type-label]').forEach((node) => {
        node.textContent = petTypeLabel;
      });
      this.querySelectorAll('[data-plan-pronoun-subject]').forEach((node) => {
        node.textContent = pronouns.subject;
      });
      this.querySelectorAll('[data-plan-pronoun-object]').forEach((node) => {
        node.textContent = pronouns.object;
      });

      this.petImageTargets.forEach((node) => {
        const dogSrc = node.dataset.dogSrc || this.dogIconUrl;
        const catSrc = node.dataset.catSrc || this.catIconUrl;
        node.src = petTypeLabel === 'cat' ? catSrc : dogSrc;
        node.alt = '';
      });

      this.querySelectorAll('.plennia-plan-option__icon').forEach((iconWrap) => {
        const bodyIcon = iconWrap.querySelector('[data-plan-body-icon]');
        if (!bodyIcon) return;

        const fallbackIcon = iconWrap.querySelector('[data-plan-body-fallback-icon]');
        const nextSrc = petTypeLabel === 'cat' ? bodyIcon.dataset.catSrc : petTypeLabel === 'dog' ? bodyIcon.dataset.dogSrc : '';
        const hasNextSrc = Boolean(nextSrc);

        if (hasNextSrc && bodyIcon.dataset.currentSrc !== nextSrc) {
          bodyIcon.dataset.currentSrc = nextSrc;
          bodyIcon.dataset.imageMissing = 'false';
          bodyIcon.src = nextSrc;
        }

        const showBodyIcon = hasNextSrc && bodyIcon.dataset.imageMissing !== 'true';
        bodyIcon.hidden = !showBodyIcon;
        if (fallbackIcon) fallbackIcon.hidden = showBodyIcon;
      });

      if (this.basicIntroTarget) {
        this.basicIntroTarget.textContent = hasPetName
          ? `You're editing ${petNamePossessive} profile.`
          : `You're building this plan for your ${petTypeLabel}.`;
      }

      if (this.emailBody) {
        this.emailBody.textContent = this.customerId
          ? `You're signed in, so we'll save ${petName}'s plan directly to your customer profile and keep it ready for later edits.`
          : "Please, enter your email so we can continue with your buddy's meal planning.";
      }

      if (this.authCopy) {
        this.authCopy.textContent = this.customerId
          ? `Signed in as ${this.customerEmail || 'this customer'}. You can still change the email if you want the plan sent elsewhere.`
          : 'You do not need to create an account before seeing the results. We will keep this plan ready and attach it when you sign in later.';
      }
    }

    updateActivityUI() {
      const activityStep = activityStepForScore(this.state.activityScore || this.activityInput?.value || 3);
      const boltsTarget = this.querySelector('[data-activity-bolts]');
      const scoreTarget = this.querySelector('[data-activity-score]');
      const levelTarget = this.querySelector('[data-activity-level]');
      const copyTarget = this.querySelector('[data-activity-copy]');

      if (this.activityInput) this.activityInput.value = String(activityStep.score);
      if (scoreTarget) scoreTarget.textContent = String(activityStep.score);
      if (levelTarget) levelTarget.textContent = activityStep.label;
      if (copyTarget) copyTarget.textContent = activityStep.description;
      if (boltsTarget) {
        boltsTarget.innerHTML = Array.from({ length: 5 }, (_, index) => {
          const isActive = index < activityStep.score;
          return `<span class="plennia-plan-activity__bolt ${isActive ? 'is-active' : ''}"></span>`;
        }).join('');
      }
    }

    goToStep(index) {
      const sequence = this.getStepSequence();
      const longFormVisibleSteps = new Set(sequence.filter((stepIndex) => stepIndex > 0));
      const nextIndex = sequence.includes(index) ? index : this.getLastStepIndex();
      this.currentStepIndex = nextIndex;
      this.isLongForm = this.currentStepIndex > 0;

      this.classList.toggle('is-long-form', this.isLongForm);
      this.dataset.currentStep = String(nextIndex);

      this.steps.forEach((step, stepIndex) => {
        const isVisible = this.isLongForm ? longFormVisibleSteps.has(stepIndex) : stepIndex === 0;
        const isActive = this.isLongForm ? stepIndex === this.currentStepIndex : isVisible;
        step.classList.toggle('is-active', isActive);
        step.toggleAttribute('hidden', !isVisible);
      });

      const stepPillsContainer = this.querySelector('[data-quiz-step-pills]');
      if (stepPillsContainer) {
        const pills = stepPillsContainer.querySelectorAll('[data-step-pill]');
        pills.forEach((pill) => {
          const pillStep = parseInt(pill.dataset.stepPill, 10);
          pill.classList.toggle('is-active', pillStep === nextIndex);
          pill.classList.toggle('is-done', pillStep < nextIndex);
        });
      }

      if (this.progressLabel) {
        this.progressLabel.textContent = this.isLongForm
          ? 'Complete your plan'
          : this.steps[this.currentStepIndex]?.dataset.stepLabel || '';
      }
      if (this.progressBar) {
        this.progressBar.style.width = this.isLongForm
          ? '100%'
          : `${((this.getStepPosition(this.currentStepIndex) + 1) / sequence.length) * 100}%`;
      }
      if (this.prevButton) this.prevButton.hidden = false;
      if (this.nextButton) {
        this.nextButton.innerHTML = this.isLongForm
          ? 'See the plan <span aria-hidden="true">&rsaquo;</span>'
          : 'Continue <span aria-hidden="true">&rsaquo;</span>';
      }
      this.updatePrimaryActionState();

      const targetStep = this.isLongForm
        ? this.steps[this.currentStepIndex]
        : this.steps[0] || this.closest('.shopify-section') || this;
      const top = targetStep.getBoundingClientRect().top + window.scrollY - 24;
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
      });
    }

    updatePrimaryActionState() {
      if (!this.nextButton) return;

      const isDisabled = this.currentStepIndex === 0 && !this.state.petType;
      this.nextButton.disabled = isDisabled;

      if (isDisabled) {
        this.nextButton.setAttribute('aria-disabled', 'true');
      } else {
        this.nextButton.removeAttribute('aria-disabled');
      }
    }

    finalizeQuiz() {
      const timestamp = new Date().toISOString();
      const recommendation = recommendProducts(this.products, this.state);
      const finalizedState = setQuizState(
        {
          ...this.state,
          last_quiz_completed_at: timestamp,
          recommended_product_handles: recommendation.recommendedHandles,
        },
        this.storageKey
      );

      const context = getQuizContext();
      const nextProfile = buildPetProfileFromQuiz(finalizedState, recommendation, {
        id: context.petProfileId || finalizedState.petId || `pet-${Date.now()}`,
      });

      if (this.customerId) {
        const pets = upsertPetProfile(readCustomerPets(this.customerId, []), nextProfile);
        writeCustomerPets(this.customerId, pets, nextProfile.id);
        setQuizContext({ petProfileId: nextProfile.id, customerId: this.customerId });
        document.dispatchEvent(
          new CustomEvent('plennia:pets:updated', { detail: { pets, activePetId: nextProfile.id } })
        );
        syncCustomerPets(this.customerId, pets, nextProfile.id, { reason: 'quiz-complete' }).then((result) => {
          if (!result.synced) return;
          writeCustomerPets(this.customerId, result.pets, result.activePetId || nextProfile.id);
          document.dispatchEvent(
            new CustomEvent('plennia:pets:updated', {
              detail: { pets: result.pets, activePetId: result.activePetId || nextProfile.id },
            })
          );
        });
      } else if (finalizedState.email) {
        persistPendingPetProfile(nextProfile);
      }

      if (this.resultsUrl) window.location.href = this.resultsUrl;
    }
  }

  class PlenniaRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.storageKey = this.dataset.storageKey || STORAGE_KEYS.quiz;
      this.currencyCode = this.dataset.currency || 'USD';
      this.customerId = this.dataset.customerId || currentCustomerIdFromDocument() || '';
      this.collectionHandle = this.dataset.sourceCollectionHandle || 'all';
      this.stateSource = this.dataset.stateSource || 'quiz';
      this.accountUrl = this.dataset.accountUrl || getThemeConfig().accountUrl || '/account';
      this.planLink = this.dataset.planLink || getThemeConfig().planUrl || '/pages/plan';
      this.products = normalizePlanProductList(getProductsFromScripts(this));
      this.resultsTarget = this.querySelector('[data-recommendation-results]');
      this.actionsTarget = this.querySelector('[data-recommendation-actions]');
      this.summaryTarget = this.querySelector('[data-recommendation-summary]');

      document.addEventListener('plennia:quiz:updated', () => this.render());
      document.addEventListener('plennia:pets:updated', () => this.render());
      this.addEventListener('click', (event) => {
        const addAllButton = event.target.closest('[data-recommendation-add-all]');
        const addItemButton = event.target.closest('[data-plennia-add-item]');

        if (addAllButton) {
          event.preventDefault();
          const items = parseJSON(addAllButton.dataset.items, []);
          addItemsToCart(items).then(() => {
            window.location.href = window.routes ? window.routes.cart_url : '/cart';
          });
        }

        if (addItemButton) {
          event.preventDefault();
          const variantId = Number(addItemButton.dataset.variantId);
          if (!variantId) return;
          addItemsToCart([{ id: variantId, quantity: 1 }]).then(() => {
            addItemButton.textContent = 'Added ✓';
            addItemButton.disabled = true;
          });
        }

        // Subscribe-all CTA (from subscription banner)
        const subscribeAllButton = event.target.closest('[data-recommendation-subscribe-all]');
        if (subscribeAllButton) {
          event.preventDefault();
          const items = parseJSON(subscribeAllButton.dataset.items, []);
          if (!items.length) return;
          addItemsToCart(items).then(() => {
            window.location.href = window.routes ? window.routes.cart_url : '/cart';
          });
        }

        // Per-card subscribe button
        const subscribeItemButton = event.target.closest('[data-plennia-subscribe-item]');
        if (subscribeItemButton) {
          event.preventDefault();
          const variantId = Number(subscribeItemButton.dataset.variantId);
          const sellingPlanId = subscribeItemButton.dataset.sellingPlanId;
          if (!variantId) return;
          const item = { id: variantId, quantity: 1 };
          if (sellingPlanId) item.selling_plan = Number(sellingPlanId) || sellingPlanId;
          addItemsToCart([item]).then(() => {
            subscribeItemButton.textContent = 'Subscribed ✓';
            subscribeItemButton.disabled = true;
          });
        }

        // Purchase type radio toggle (once / subscribe)
        const purchaseTypeRadio = event.target.closest('input[type="radio"][name^="purchase-type-"]');
        if (purchaseTypeRadio) {
          const cardId = purchaseTypeRadio.name.replace('purchase-type-', '');
          const mode = purchaseTypeRadio.value; // 'once' | 'subscribe'
          const card = this.querySelector(`[data-card-id="${cardId}"]`);
          if (!card) return;

          // Toggle price blocks
          const priceOnce = card.querySelector(`[data-price-once="${cardId}"]`);
          const priceSub = card.querySelector(`[data-price-subscribe="${cardId}"]`);
          if (priceOnce) priceOnce.hidden = mode === 'subscribe';
          if (priceSub) priceSub.hidden = mode === 'once';

          // Toggle action buttons
          const btnOnce = card.querySelector('[data-plennia-add-item]');
          const btnSub = card.querySelector('[data-plennia-subscribe-item]');
          if (btnOnce) btnOnce.hidden = mode === 'subscribe';
          if (btnSub) btnSub.hidden = mode === 'once';

          // Update toggle label active class
          card.querySelectorAll('.plennia-plan-product-card__type-option').forEach((label) => {
            label.classList.toggle('plennia-plan-product-card__type-option--active', label.querySelector('input')?.value === mode);
          });
        }
      });

      this.render();
      this.loadProductCatalog();

      if (this.customerId) {
        hydrateCustomerPets(this.customerId, [], { scope: this }).then(() => this.render());
      }
    }

    loadProductCatalog() {
      fetchCollectionProducts(this.collectionHandle)
        .then((products) => {
          const normalizedProducts = normalizePlanProductList(products);
          if (!normalizedProducts.length) return;
          this.products = normalizedProducts;
          this.render();
        })
        .catch(() => {
          return;
        });
    }

    resolveState() {
      if (this.stateSource === 'active_pet') {
        const activePet = getActivePetProfile(this.customerId);
        return activePet ? buildQuizStateFromPetProfile(activePet) : null;
      }
      return getQuizState(this.storageKey);
    }

    render() {
      if (!this.resultsTarget) return;
      const state = this.resolveState();

      if (!state || !state.petType) {
        if (this.summaryTarget) this.summaryTarget.innerHTML = '';
        if (this.actionsTarget) this.actionsTarget.innerHTML = '';
        this.resultsTarget.innerHTML =
          this.stateSource === 'active_pet'
            ? `<article class="plennia-dashboard-card plennia-pet-card"><div class="plennia-card__copy"><h3 class="plennia-card__title">Choose a pet to unlock recommendations</h3><p class="plennia-card__excerpt">Select or create a pet profile in your account, then rerun Create a Plan to keep recommendations tied to that pet.</p><div class="plennia-pet-card__actions"><a class="button" href="${this.accountUrl}">Manage pets</a><a class="button button--secondary plennia-button-secondary" href="${this.planLink}">Create a plan</a></div></div></article>`
            : `<article class="plennia-dashboard-card plennia-pet-card"><div class="plennia-card__copy"><h3 class="plennia-card__title">Create a plan to unlock recommendations</h3><p class="plennia-card__excerpt">Answer the quiz first, then this block will rank the real catalog from Shopify product tags.</p><div class="plennia-pet-card__actions"><a class="button" href="${this.planLink}">Create a plan</a></div></div></article>`;
        return;
      }

      const recommendation = recommendProducts(this.products, state);
      if (this.summaryTarget) {
        this.summaryTarget.innerHTML = buildSummaryChips(state)
          .map((chip) => `<span class="plennia-badge plennia-badge--muted">${chip}</span>`)
          .join('');
      }

      if (!recommendation.hasResults) {
        this.resultsTarget.innerHTML = `<article class="plennia-dashboard-card plennia-pet-card"><div class="plennia-card__copy"><h3 class="plennia-card__title">No matching products yet</h3><p class="plennia-card__excerpt">Keep the pet profile saved, then add more tagged products to the catalog and this block will pick them up automatically.</p></div></article>`;
        if (this.actionsTarget) this.actionsTarget.innerHTML = '';
        return;
      }

      this.resultsTarget.innerHTML = renderSlotsMarkup(recommendation, this.currencyCode, state);

      if (this.actionsTarget) {
        const onceItems = buildCartItems(recommendation);
        const subItems = buildSubscriptionItems(recommendation);
        const subAvailable = subItems.length > 0;
        const addAllBtn = onceItems.length
          ? `<button class="button" type="button" data-recommendation-add-all data-items='${JSON.stringify(onceItems)}'>Add top picks to cart</button>`
          : '';
        const subAllBtn = subAvailable
          ? `<button class="button button--secondary plennia-button-secondary" type="button" data-recommendation-subscribe-all data-items='${JSON.stringify(subItems)}'>Subscribe &amp; Save ${DEFAULT_SUBSCRIPTION_DISCOUNT}%</button>`
          : '';
        this.actionsTarget.innerHTML = (addAllBtn || subAllBtn)
          ? `<div class="plennia-plan-results-actions">${addAllBtn}${subAllBtn}</div>`
          : '';
      }
    }
  }

  class PlenniaPetDashboard extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.customerId = this.dataset.customerId || 'guest';
      this.collectionHandle = this.dataset.sourceCollectionHandle || 'all';
      this.flashTarget = this.querySelector('[data-pet-save-flash]');
      this.switcherTarget = this.querySelector('[data-pet-switcher-controls]');
      this.cardsTarget = this.querySelector('[data-pet-cards]');
      this.form = this.querySelector('[data-pet-form]');
      this.overviewTarget = this.querySelector('[data-pet-dashboard-overview]');
      this.activePetSummaryTarget = this.querySelector('[data-active-pet-summary]');
      this.addTriggers = Array.from(this.querySelectorAll('[data-pet-add-trigger]'));
      this.formFields = {
        id: this.querySelector('[name="pet_id"]'),
        name: this.querySelector('[name="pet_profile_name"]'),
        type: this.querySelector('[name="pet_profile_type"]'),
        age: this.querySelector('[name="pet_profile_age"]'),
        breed: this.querySelector('[name="pet_profile_breed"]'),
        weight: this.querySelector('[name="pet_profile_weight"]'),
      };
      this.savedPlanTarget = this.querySelector('[data-saved-plan-summary]');
      this.products = normalizePlanProductList(getProductsFromScripts(this));
      this.initialPets = getInitialPetProfiles(this);
      this.pets = readCustomerPets(this.customerId, this.initialPets);
      this.activePetId = readStorage(
        getPetStorageKeys(this.customerId).activePet,
        getInitialActivePetId(this) || (this.pets[0] ? this.pets[0].id : null)
      );
      this.importPendingProfile();

      this.bindEvents();
      this.render();
      this.loadProductCatalog();

      if (this.customerId && this.customerId !== 'guest') {
        hydrateCustomerPets(this.customerId, this.initialPets, { scope: this }).then((result) => {
          if (!result.hydrated) return;
          this.pets = normalizePetProfiles(result.pets || this.pets);
          this.activePetId = result.activePetId || this.activePetId;
          this.render();
        });
      }
    }

    loadProductCatalog() {
      fetchCollectionProducts(this.collectionHandle)
        .then((products) => {
          const normalizedProducts = normalizePlanProductList(products);
          if (!normalizedProducts.length) return;
          this.products = normalizedProducts;
          this.render();
        })
        .catch(() => {
          return;
        });
    }

    bindEvents() {
      this.addEventListener('click', (event) => {
        const addTrigger = event.target.closest('[data-pet-add-trigger]');
        const switchTrigger = event.target.closest('[data-pet-switch]');
        const editTrigger = event.target.closest('[data-pet-edit]');
        const deleteTrigger = event.target.closest('[data-pet-delete]');
        const cancelTrigger = event.target.closest('[data-pet-cancel]');
        const viewTrigger = event.target.closest('[data-pet-view]');
        const planTrigger = event.target.closest('[data-pet-plan]');

        if (addTrigger) {
          event.preventDefault();
          this.openForm();
        }
        if (switchTrigger) {
          event.preventDefault();
          this.activePetId = switchTrigger.dataset.petSwitch;
          writeStorage(getPetStorageKeys(this.customerId).activePet, this.activePetId);
          this.render();
        }
        if (editTrigger) {
          event.preventDefault();
          this.openForm(editTrigger.dataset.petEdit);
        }
        if (deleteTrigger) {
          event.preventDefault();
          this.deletePet(deleteTrigger.dataset.petDelete);
        }
        if (viewTrigger) {
          event.preventDefault();
          this.setActivePet(viewTrigger.dataset.petView);
          window.location.href = viewTrigger.getAttribute('href') || '/pages/pet';
        }
        if (planTrigger) this.setActivePet(planTrigger.dataset.petPlan);
        if (cancelTrigger) {
          event.preventDefault();
          this.closeForm();
        }
      });

      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.savePet();
        });
      }
    }

    openForm(petId) {
      if (!this.form) return;
      const pet = this.pets.find((item) => item.id === petId);

      if (pet) {
        this.formFields.id.value = pet.id;
        this.formFields.name.value = pet.petName || pet.name || '';
        this.formFields.type.value = pet.petType || pet.type || 'dog';
        this.formFields.age.value = pet.petAge || pet.age || '';
        this.formFields.breed.value = pet.petBreed || pet.breed || '';
        this.formFields.weight.value = pet.petWeight || pet.weight || '';
      } else {
        this.form.reset();
        this.formFields.id.value = '';
        this.formFields.type.value = 'dog';
      }

      this.form.classList.add('is-visible');
    }

    closeForm() {
      if (!this.form) return;
      this.form.classList.remove('is-visible');
      this.form.reset();
      this.formFields.id.value = '';
      this.formFields.type.value = 'dog';
    }

    setActivePet(petId) {
      this.activePetId = petId;
      writeStorage(getPetStorageKeys(this.customerId).activePet, this.activePetId);
      setQuizContext({ petProfileId: petId || '', customerId: this.customerId || '' });
      this.render();
    }

    importPendingProfile() {
      if (!this.customerId || this.customerId === 'guest') return;
      const pendingProfile = consumePendingPetProfile();
      if (!pendingProfile) return;

      this.pets = upsertPetProfile(this.pets, pendingProfile);
      this.activePetId = pendingProfile.id;
      this.persist();
      this.showFlash(`${pendingProfile.petName || pendingProfile.name} was saved as a pet profile in this account.`);
    }

    showFlash(message) {
      if (!this.flashTarget || !message) return;
      this.flashTarget.hidden = false;
      this.flashTarget.innerHTML = `<p>${message}</p>`;
    }

    getActivePet() {
      return this.pets.find((pet) => pet.id === this.activePetId) || this.pets[0] || null;
    }

    savePet() {
      const petId = this.formFields.id.value || `pet-${Date.now()}`;
      const existingPet = this.pets.find((pet) => pet.id === petId);
      const petName = this.formFields.name.value.trim();

      if (!petName) {
        this.showFlash('Add a pet name before saving the profile.');
        return;
      }

      const nextQuizAnswers = normalizeQuizState({
        ...(existingPet?.savedQuizAnswers || {}),
        pet_id: petId,
        pet_type: this.formFields.type.value,
        pet_name: petName,
        pet_age: this.formFields.age.value.trim(),
        pet_breed: this.formFields.breed.value.trim(),
        pet_weight: this.formFields.weight.value.trim(),
        pet_age_group: mapAgeGroup(this.formFields.type.value, this.formFields.age.value.trim()),
      });

      const recommendation = recommendProducts(this.products.length ? this.products : getProductsFromScripts(document), nextQuizAnswers);
      const nextPet = buildPetProfileFromQuiz(nextQuizAnswers, recommendation, {
        id: petId,
        createdAt: existingPet?.createdAt,
        email: existingPet?.email || '',
      });

      this.pets = upsertPetProfile(this.pets, nextPet);
      this.activePetId = petId;
      this.persist();
      this.closeForm();
      this.showFlash(`${nextPet.petName || nextPet.name} is ready inside this account.`);
      this.render();
    }

    deletePet(petId) {
      this.pets = this.pets.filter((pet) => pet.id !== petId);
      if (this.activePetId === petId) this.activePetId = this.pets[0] ? this.pets[0].id : null;
      this.persist();
      this.showFlash('Pet profile deleted.');
      this.render();
    }

    persist() {
      writeCustomerPets(this.customerId, this.pets, this.activePetId);
      document.dispatchEvent(
        new CustomEvent('plennia:pets:updated', { detail: { pets: this.pets, activePetId: this.activePetId } })
      );

      syncCustomerPets(this.customerId, this.pets, this.activePetId, { reason: 'dashboard-save' }).then((result) => {
        if (!result.synced) return;
        this.pets = normalizePetProfiles(result.pets || this.pets);
        this.activePetId = result.activePetId || this.activePetId;
        writeCustomerPets(this.customerId, this.pets, this.activePetId);
        document.dispatchEvent(
          new CustomEvent('plennia:pets:updated', { detail: { pets: this.pets, activePetId: this.activePetId } })
        );
        this.render();
      });
    }

    renderOverview() {
      if (!this.overviewTarget) return;
      const activePet = this.getActivePet();
      const savedRecommendations = this.pets.filter((pet) => petHasSavedPlan(pet)).length;

      this.overviewTarget.innerHTML = `
        <div class="plennia-note-list__item">
          <span>Saved pets</span>
          <span>${this.pets.length}</span>
        </div>
        <div class="plennia-note-list__item">
          <span>Active pet</span>
          <span>${activePet ? activePet.petName || activePet.name : 'None yet'}</span>
        </div>
        <div class="plennia-note-list__item">
          <span>Saved recommendations</span>
          <span>${savedRecommendations}</span>
        </div>
      `;
    }

    renderActivePetSummary() {
      if (!this.activePetSummaryTarget) return;
      const activePet = this.getActivePet();

      if (!activePet) {
        this.activePetSummaryTarget.innerHTML = `
          <p class="plennia-section__eyebrow">Active pet</p>
          <h2 class="plennia-card__title">No pet selected yet</h2>
          <p class="plennia-card__excerpt">Add the first pet profile, then run Create a Plan and keep the saved recommendation attached to that pet.</p>
          <div class="plennia-pet-card__actions">
            <button class="button" type="button" data-pet-add-trigger>Add first pet</button>
          </div>
        `;
        return;
      }

      const summary = [
        activePet.petAge ? `${activePet.petAge} years old` : '',
        activePet.petBreed || '',
        activePet.petWeight ? `${activePet.petWeight} kg` : '',
      ]
        .filter(Boolean)
        .join(' / ');
      const planReady = petHasSavedPlan(activePet);
      const quizLabel = petHasSavedQuiz(activePet) ? 'Continue quiz' : 'Start quiz';

      this.activePetSummaryTarget.innerHTML = `
        <p class="plennia-section__eyebrow">Active pet</p>
        <div class="plennia-pet-card__head">
          <span class="plennia-pet-card__icon">${iconForPetType(activePet.petType || activePet.type)}</span>
          <div class="plennia-card__badges">
            <span class="plennia-badge">${capitalize(activePet.petType || activePet.type)}</span>
            <span class="plennia-badge plennia-badge--muted">Selected</span>
          </div>
        </div>
        <div class="plennia-card__copy">
          <h2 class="plennia-card__title">${activePet.petName || activePet.name}</h2>
          <p class="plennia-card__excerpt">${summary || 'Finish the pet basics and continue into the quiz.'}</p>
        </div>
        <div class="plennia-pet-card__actions">
          <a class="button" href="/pages/plan?pet=${encodeURIComponent(activePet.id)}" data-pet-plan="${activePet.id}">${quizLabel}</a>
          ${
            planReady
              ? `<a class="button button--secondary plennia-button-secondary" href="/pages/pet" data-pet-view="${activePet.id}">View recommendation</a>`
              : ''
          }
        </div>
      `;
    }

    renderSavedPlan() {
      if (!this.savedPlanTarget) return;
      const activePet = this.getActivePet();

      if (!activePet) {
        this.savedPlanTarget.innerHTML = '<p class="plennia-note">No pet selected yet.</p>';
        return;
      }

      if (!petHasSavedPlan(activePet)) {
        this.savedPlanTarget.innerHTML = `
          <div class="plennia-card__copy">
            <h3 class="plennia-card__title">No recommendation saved yet</h3>
            <p class="plennia-card__excerpt">Choose ${activePet.petName || activePet.name} and run the quiz to save the first recommendation set for this pet.</p>
          </div>
          <div class="plennia-pet-card__actions">
            <a class="button" href="/pages/plan?pet=${encodeURIComponent(activePet.id)}" data-pet-plan="${activePet.id}">Start quiz</a>
          </div>
        `;
        return;
      }

      const recommendation = recommendProducts(
        this.products.length ? this.products : getProductsFromScripts(document),
        activePet.savedQuizAnswers || buildQuizStateFromPetProfile(activePet)
      );
      const savedPlan = activePet.savedRecommendedPlan || buildSavedPlan(activePet.savedQuizAnswers, recommendation);

      this.savedPlanTarget.innerHTML = `
        <div class="plennia-card__copy">
          <h3 class="plennia-card__title">${activePet.petName || activePet.name}</h3>
          <p class="plennia-card__excerpt">Each pet keeps its own quiz answers and saved recommendation set.</p>
        </div>
        ${renderSavedPlanMarkup(savedPlan)}
      `;
    }

    render() {
      this.renderOverview();
      this.renderActivePetSummary();

      this.addTriggers.forEach((trigger) => {
        trigger.textContent = this.pets.length ? 'Add another pet' : 'Add first pet';
      });

      if (this.switcherTarget) {
        this.switcherTarget.innerHTML = this.pets.length
          ? this.pets
              .map(
                (pet) =>
                  `<button class="plennia-pet-switcher__button ${pet.id === this.activePetId ? 'is-active' : ''}" type="button" data-pet-switch="${pet.id}">${iconForPetType(pet.petType || pet.type)} ${pet.petName || pet.name}</button>`
              )
              .join('')
          : '<p class="plennia-note">No pet profiles yet. Add the first profile to start saving plans by pet.</p>';
      }

      if (this.cardsTarget) {
        this.cardsTarget.innerHTML = this.pets.length
          ? this.pets
              .map((pet) => {
                const summary = [
                  pet.petAge ? `${pet.petAge} years` : '',
                  pet.petBreed || 'Breed pending',
                  pet.petWeight ? `${pet.petWeight} kg` : '',
                ]
                  .filter(Boolean)
                  .join(' / ');
                const isActive = pet.id === this.activePetId;
                const savedPlan = pet.savedRecommendedPlan || buildSavedPlan(
                  pet.savedQuizAnswers,
                  recommendProducts(
                    this.products.length ? this.products : getProductsFromScripts(document),
                    pet.savedQuizAnswers || buildQuizStateFromPetProfile(pet)
                  )
                );
                const planChips = arrayify(savedPlan?.chips).slice(0, 4);
                const planReady = petHasSavedPlan(pet);
                const quizLabel = petHasSavedQuiz(pet) ? 'Continue quiz' : 'Start quiz';
                const statusLabel = planReady ? 'Recommendation saved' : 'Profile ready for quiz';

                return `
                  <article class="plennia-dashboard-card plennia-pet-card ${isActive ? 'is-active' : ''}">
                    <div class="plennia-pet-card__head">
                      <div><span class="plennia-pet-card__icon">${iconForPetType(pet.petType || pet.type)}</span></div>
                      <div class="plennia-card__badges">
                        <span class="plennia-badge">${capitalize(pet.petType || pet.type)}</span>
                        ${isActive ? '<span class="plennia-badge plennia-badge--muted">Active pet</span>' : ''}
                      </div>
                    </div>
                    <div class="plennia-card__copy">
                      <h3 class="plennia-card__title">${pet.petName || pet.name}</h3>
                      <p class="plennia-card__excerpt">${summary}</p>
                      <p class="plennia-pet-card__status">${statusLabel}</p>
                    </div>
                    ${
                      planChips.length
                        ? `<div class="plennia-saved-plan">${planChips
                            .map((chip) => `<span class="plennia-badge plennia-badge--muted">${chip}</span>`)
                            .join('')}</div>`
                        : '<p class="plennia-note">No saved recommendation yet. Start the quiz for this pet.</p>'
                    }
                    <div class="plennia-pet-card__actions">
                      <a class="button" href="/pages/plan?pet=${encodeURIComponent(pet.id)}" data-pet-plan="${pet.id}">${quizLabel}</a>
                      ${
                        planReady
                          ? `<a class="button button--secondary plennia-button-secondary" href="/pages/pet" data-pet-view="${pet.id}">View recommendation</a>`
                          : ''
                      }
                      <button class="button button--secondary plennia-button-secondary" type="button" data-pet-edit="${pet.id}">Edit</button>
                      <button class="plennia-link plennia-link--danger" type="button" data-pet-delete="${pet.id}">Delete</button>
                    </div>
                  </article>
                `;
              })
              .join('')
          : `
            <article class="plennia-dashboard-card plennia-pet-card plennia-account__empty">
              <div class="plennia-card__copy">
                <h3 class="plennia-card__title">No pets saved yet</h3>
                <p class="plennia-card__excerpt">Create the first pet profile, then run the quiz and keep every saved recommendation tied to that pet.</p>
              </div>
              <div class="plennia-pet-card__actions">
                <button class="button" type="button" data-pet-add-trigger>Add first pet</button>
              </div>
            </article>
          `;
      }

      this.renderSavedPlan();
    }
  }

  class PlenniaPetProfile extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.customerId = this.dataset.customerId || 'guest';
      this.panel = this.querySelector('[data-pet-profile-panel]');
      this.initialPets = getInitialPetProfiles(this);
      this.render();
      document.addEventListener('plennia:pets:updated', () => this.render());

      if (this.customerId && this.customerId !== 'guest') {
        hydrateCustomerPets(this.customerId, this.initialPets, { scope: this }).then(() => this.render());
      }
    }

    render() {
      if (!this.panel) return;
      const pets = readCustomerPets(this.customerId, this.initialPets);
      const activePetId = readStorage(
        getPetStorageKeys(this.customerId).activePet,
        getInitialActivePetId(this) || (pets[0] ? pets[0].id : null)
      );
      const activePet = pets.find((pet) => pet.id === activePetId) || pets[0];

      if (!activePet) {
        this.panel.innerHTML = '<article class="plennia-dashboard-card plennia-pet-card"><div class="plennia-card__copy"><h2 class="plennia-card__title">No active pet selected</h2><p class="plennia-card__excerpt">Add a pet profile from the account dashboard to seed this page with pet-specific plans and recommendation history.</p></div></article>';
        return;
      }

      const infoRows = [
        ['Pet type', capitalize(activePet.petType || activePet.type)],
        ['Sex', activePet.petSex ? capitalize(activePet.petSex) : 'Pending'],
        ['Age', activePet.petAge ? `${activePet.petAge} years` : 'Pending'],
        ['Age group', activePet.petAgeGroup ? labelFromMap('age_group', activePet.petAgeGroup) : 'Pending'],
        ['Breed', activePet.petBreed || 'Pending'],
        ['Weight', activePet.petWeight ? `${activePet.petWeight} kg` : 'Pending'],
        ['Neutered / spayed', activePet.petNeutered ? (activePet.petNeutered === 'yes' ? 'Yes' : 'No') : 'Pending'],
        ['Body condition', activePet.bodyCondition ? labelFromMap('body_condition', activePet.bodyCondition) : 'Pending'],
        ['Activity level', activePet.activityLevel ? labelFromMap('activity_level', activePet.activityLevel) : 'Pending'],
        ['Current food', arrayify(activePet.currentFoodTypes).length ? arrayify(activePet.currentFoodTypes).map((item) => labelFromMap('food_types', item)).join(', ') : 'Pending'],
        ['Primary goal', arrayify(activePet.primaryGoals).length ? readableList(arrayify(activePet.primaryGoals).map((item) => labelFromMap('primary_goal', item))) : 'General wellbeing'],
        ['Health issues', arrayify(activePet.healthIssues).length ? arrayify(activePet.healthIssues).map((item) => labelFromMap('health_issues', item)).join(', ') : 'None listed'],
        ['Ingredient allergies', arrayify(activePet.ingredientAllergies).length ? arrayify(activePet.ingredientAllergies).map((item) => labelFromMap('allergies', item)).join(', ') : 'None listed'],
      ];
      const savedPlan = activePet.savedRecommendedPlan || buildSavedPlan(activePet.savedQuizAnswers, recommendProducts(getProductsFromScripts(document), activePet.savedQuizAnswers || buildQuizStateFromPetProfile(activePet)));

      this.panel.innerHTML = `
        <article class="plennia-dashboard-card plennia-pet-card is-active">
          <div class="plennia-pet-card__head">
            <span class="plennia-pet-card__icon">${iconForPetType(activePet.petType || activePet.type)}</span>
            <div class="plennia-card__badges">
              <span class="plennia-badge">${capitalize(activePet.petType || activePet.type)}</span>
              <span class="plennia-badge plennia-badge--muted">Current profile</span>
            </div>
          </div>
          <div class="plennia-card__copy">
            <h2 class="plennia-card__title">${activePet.petName || activePet.name}</h2>
            <p class="plennia-card__excerpt">${[activePet.petAge ? `${activePet.petAge} years` : '', activePet.petBreed || 'Breed pending'].filter(Boolean).join(' / ')}</p>
          </div>
          <div class="plennia-pet-card__actions">
            <a class="plennia-link" href="/pages/plan?pet=${encodeURIComponent(activePet.id)}">${petHasSavedQuiz(activePet) ? 'Continue quiz' : 'Start quiz'}</a>
            <a class="plennia-link" href="/account">Back to dashboard</a>
          </div>
          <div class="plennia-note-list">
            ${infoRows.map(([label, value]) => `<div class="plennia-note-list__item"><span>${label}</span><span>${value}</span></div>`).join('')}
          </div>
          <div class="plennia-pet-card__plan">${renderSavedPlanMarkup(savedPlan)}</div>
        </article>
      `;
    }
  }

  class PlenniaPlanResultsHero extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.customerId = this.dataset.customerId || '';
      this.customerEmail = this.dataset.customerEmail || '';
      this.titleTarget = this.querySelector('[data-plan-results-title]');
      this.bodyTarget = this.querySelector('[data-plan-results-body]');
      this.chipsTarget = this.querySelector('[data-plan-results-chips]');
      this.statusTarget = this.querySelector('[data-plan-results-status]');
      this.render();
      document.addEventListener('plennia:quiz:updated', () => this.render());
    }

    render() {
      const state = getQuizState(STORAGE_KEYS.quiz);
      if (!state.petType) return;
      const recommendation = recommendProducts(getProductsFromScripts(document), state);
      const copy = buildResultsCopy(state, recommendation);

      if (this.titleTarget) this.titleTarget.textContent = copy.title;
      if (this.bodyTarget) this.bodyTarget.textContent = copy.body;
      if (this.chipsTarget) this.chipsTarget.innerHTML = '';
      if (this.statusTarget) {
        this.statusTarget.textContent = this.customerId
          ? `This plan is linked to ${this.customerEmail || 'your customer account'} and updates per pet profile.`
          : state.email
            ? `This guest plan is stored for ${state.email} and can be attached when you sign in later.`
            : copy.status;
      }
    }
  }

  class PlenniaPlanResultsSummary extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.planLink = this.dataset.planLink || '/pages/plan';
      this.accountUrl = this.dataset.accountUrl || '/account';
      this.loginUrl = this.dataset.loginUrl || '/account/login';
      this.registerUrl = this.dataset.registerUrl || '/account/register';
      this.customerId = this.dataset.customerId || '';
      this.initialPets = getInitialPetProfiles(this);
      this.titleTarget = this.querySelector('[data-results-summary-title]');
      this.bodyTarget = this.querySelector('[data-results-summary-body]');
      this.chipsTarget = this.querySelector('[data-results-summary-chips]');
      this.profileTarget = this.querySelector('[data-results-profile-summary]');
      this.saveTarget = this.querySelector('[data-results-profile-save]');
      this.saveMessage = '';
      this.lastPersistSignature = '';
      this.render();
      document.addEventListener('plennia:quiz:updated', () => this.render());

      if (this.customerId) {
        hydrateCustomerPets(this.customerId, this.initialPets, { scope: this }).then(() => this.render());
      }
    }

    buildCurrentProfile(recommendation) {
      const state = getQuizState(STORAGE_KEYS.quiz);
      const context = getQuizContext();
      const existingPet =
        this.customerId && context.petProfileId
          ? readCustomerPets(this.customerId, this.initialPets).find((pet) => pet.id === context.petProfileId) || null
          : null;

      return buildPetProfileFromQuiz(state, recommendation, {
        id: context.petProfileId || state.petId || existingPet?.id || `pet-${Date.now()}`,
        createdAt: existingPet?.createdAt,
      });
    }

    ensurePersisted(profile) {
      const signature = `${profile.id}:${profile.lastQuizCompletedAt}:${arrayify(profile.recommendedProductHandles).join(',')}`;
      if (!signature || signature === this.lastPersistSignature) return;
      this.lastPersistSignature = signature;

      if (this.customerId) {
        const pets = upsertPetProfile(readCustomerPets(this.customerId, this.initialPets), profile);
        writeCustomerPets(this.customerId, pets, profile.id);
        setQuizContext({ petProfileId: profile.id, customerId: this.customerId });
        document.dispatchEvent(new CustomEvent('plennia:pets:updated', { detail: { pets, activePetId: profile.id } }));
        syncCustomerPets(this.customerId, pets, profile.id, { reason: 'results-save' }).then((result) => {
          if (!result.synced) return;
          writeCustomerPets(this.customerId, result.pets, result.activePetId || profile.id);
          document.dispatchEvent(new CustomEvent('plennia:pets:updated', { detail: { pets: result.pets, activePetId: result.activePetId || profile.id } }));
        });
        this.saveMessage = `${profile.petName || profile.name} was saved to this customer profile.`;
      } else if (profile.email) {
        persistPendingPetProfile(profile);
        this.saveMessage = `We saved this guest plan for ${profile.email}. Sign in later to attach it to a customer profile.`;
      }
    }

    renderProfileSummary(profile) {
      if (!this.profileTarget) return;
      const rows = [
        ['Pet', profile.petName || profile.name],
        ['Species', capitalize(profile.petType || profile.type)],
        ['Age group', profile.petAgeGroup ? labelFromMap('age_group', profile.petAgeGroup) : 'Pending'],
        ['Breed', profile.petBreed || 'Pending'],
        ['Weight', profile.petWeight ? `${profile.petWeight} kg` : 'Pending'],
        ['Body condition', profile.bodyCondition ? labelFromMap('body_condition', profile.bodyCondition) : 'Pending'],
        ['Activity', profile.activityLevel ? labelFromMap('activity_level', profile.activityLevel) : 'Pending'],
        ['Current food', arrayify(profile.currentFoodTypes).length ? arrayify(profile.currentFoodTypes).map((item) => labelFromMap('food_types', item)).join(', ') : 'Pending'],
        ['Main objective', arrayify(profile.primaryGoals).length ? readableList(arrayify(profile.primaryGoals).map((item) => labelFromMap('primary_goal', item))) : 'General wellbeing'],
      ];

      this.profileTarget.innerHTML = rows
        .map(([label, value]) => `<div class="plennia-plan-results-summary__profile-item"><strong>${label}</strong><span>${value}</span></div>`)
        .join('');
    }

    renderSavePanel(profile) {
      if (!this.saveTarget) return;

      if (this.customerId) {
        this.saveTarget.innerHTML = `<div class="plennia-plan-results-summary__save-card"><strong>Saved automatically</strong><p>${profile.petName || profile.name} now lives inside this Shopify customer account with support for multiple pet profiles.</p><p>${customerPetsStorageMessage()}</p><div class="plennia-plan-results-summary__save-actions"><a class="button" href="${this.accountUrl}">View account</a><a class="button button--secondary plennia-button-secondary" href="${this.planLink}">Run the quiz again</a></div>${this.saveMessage ? `<p class="plennia-plan-results-summary__save-message">${this.saveMessage}</p>` : ''}</div>`;
        return;
      }

      this.saveTarget.innerHTML = `<div class="plennia-plan-results-summary__save-card"><strong>Guest plan saved</strong><p>${profile.email ? `We stored ${profile.petName || profile.name}'s plan for ${profile.email}.` : 'Add an email in the quiz to keep a guest plan ready for later.'}</p><p>${customerPetsStorageMessage()}</p><div class="plennia-plan-results-summary__save-actions"><a class="button" href="${this.registerUrl}">Create account and attach</a><a class="button button--secondary plennia-button-secondary" href="${this.loginUrl}">Sign in to attach</a></div>${this.saveMessage ? `<p class="plennia-plan-results-summary__save-message">${this.saveMessage}</p>` : ''}</div>`;
    }

    render() {
      const state = getQuizState(STORAGE_KEYS.quiz);

      if (!state.petType) {
        if (this.titleTarget) this.titleTarget.textContent = 'No saved plan yet.';
        if (this.bodyTarget) this.bodyTarget.innerHTML = `Start the quiz first, then return here for a tailored results page. <a href="${this.planLink}">Create a plan</a>`;
        if (this.chipsTarget) this.chipsTarget.innerHTML = '';
        if (this.profileTarget) this.profileTarget.innerHTML = '';
        if (this.saveTarget) this.saveTarget.innerHTML = '';
        return;
      }

      const recommendation = recommendProducts(getProductsFromScripts(document), state);
      const copy = buildResultsCopy(state, recommendation);
      const profile = this.buildCurrentProfile(recommendation);
      this.ensurePersisted(profile);

      if (this.titleTarget) this.titleTarget.textContent = copy.title;
      if (this.bodyTarget) this.bodyTarget.textContent = copy.body;
      if (this.chipsTarget) this.chipsTarget.innerHTML = '';

      this.renderProfileSummary(profile);
      this.renderSavePanel(profile);
    }
  }

  class PlenniaPlanResultsProducts extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.storageKey = STORAGE_KEYS.quiz;
      this.currencyCode = this.dataset.currency || 'USD';
      this.planLink = this.dataset.planLink || '/pages/plan';
      this.collectionHandle = this.dataset.sourceCollectionHandle || 'all';
      this.products = normalizePlanProductList(getProductsFromScripts(this));
      this.groupsTarget = this.querySelector('[data-results-products-groups]');

      this.addEventListener('click', (event) => {
        const addItemButton = event.target.closest('[data-plennia-add-item]');
        const subscribeItemButton = event.target.closest('[data-plennia-subscribe-item]');
        const subscribeAllButton = event.target.closest('[data-recommendation-subscribe-all]');
        const purchaseTypeRadio = event.target.closest('input[type="radio"][name^="purchase-type-"]');

        if (addItemButton) {
          event.preventDefault();
          const variantId = Number(addItemButton.dataset.variantId);
          if (!variantId) return;
          addItemsToCart([{ id: variantId, quantity: 1 }]).then(() => {
            addItemButton.textContent = 'Added ✓';
            addItemButton.disabled = true;
          });
        }

        if (subscribeItemButton) {
          event.preventDefault();
          const variantId = Number(subscribeItemButton.dataset.variantId);
          const sellingPlanId = subscribeItemButton.dataset.sellingPlanId;
          if (!variantId) return;
          const item = { id: variantId, quantity: 1 };
          if (sellingPlanId) item.selling_plan = Number(sellingPlanId) || sellingPlanId;
          addItemsToCart([item]).then(() => {
            subscribeItemButton.textContent = 'Subscribed ✓';
            subscribeItemButton.disabled = true;
          });
        }

        if (subscribeAllButton) {
          event.preventDefault();
          const items = parseJSON(subscribeAllButton.dataset.items, []);
          if (!items.length) return;
          addItemsToCart(items).then(() => {
            window.location.href = window.routes ? window.routes.cart_url : '/cart';
          });
        }

        if (purchaseTypeRadio) {
          const cardId = purchaseTypeRadio.name.replace('purchase-type-', '');
          const mode = purchaseTypeRadio.value;
          const card = this.querySelector(`[data-card-id="${cardId}"]`);
          if (!card) return;
          const priceOnce = card.querySelector(`[data-price-once="${cardId}"]`);
          const priceSub = card.querySelector(`[data-price-subscribe="${cardId}"]`);
          if (priceOnce) priceOnce.hidden = mode === 'subscribe';
          if (priceSub) priceSub.hidden = mode === 'once';
          const btnOnce = card.querySelector('[data-plennia-add-item]');
          const btnSub = card.querySelector('[data-plennia-subscribe-item]');
          if (btnOnce) btnOnce.hidden = mode === 'subscribe';
          if (btnSub) btnSub.hidden = mode === 'once';
          card.querySelectorAll('.plennia-plan-product-card__type-option').forEach((label) => {
            label.classList.toggle('plennia-plan-product-card__type-option--active', label.querySelector('input')?.value === mode);
          });
        }
      });

      this.render();
      document.addEventListener('plennia:quiz:updated', () => this.render());
      this.loadProductCatalog();
    }

    loadProductCatalog() {
      fetchCollectionProducts(this.collectionHandle)
        .then((products) => {
          const normalizedProducts = normalizePlanProductList(products);
          if (!normalizedProducts.length) return;
          this.products = normalizedProducts;
          this.render();
        })
        .catch(() => {
          return;
        });
    }

    render() {
      if (!this.groupsTarget) return;
      const state = getQuizState(this.storageKey);

      if (!state.petType) {
        this.groupsTarget.innerHTML = `<div class="plennia-plan-results-products__empty"><h3>Start with the quiz to unlock your tailored products.</h3><p>Complete Create a Plan first, then this page will group real Shopify products into dry food, wet food, and treats for your pet.</p><a class="button" href="${this.planLink}">Create a plan</a></div>`;
        return;
      }

      const recommendation = recommendProducts(this.products, state);
      if (!recommendation.hasResults) {
        this.groupsTarget.innerHTML = `<div class="plennia-plan-results-products__empty"><h3>No tagged products are ready for this pet yet.</h3><p>Check that the matching Shopify products include the correct pet and format tags, then this page will surface them automatically.</p></div>`;
        return;
      }

      this.groupsTarget.innerHTML = renderRecommendationGroupsMarkup(recommendation, this.currencyCode);
    }
  }

  window.PlenniaPlanQuizClasses = {
    PlenniaQuiz,
    PlenniaRecommendations,
    PlenniaPetDashboard,
    PlenniaPetProfile,
    PlenniaPlanResultsHero,
    PlenniaPlanResultsSummary,
    PlenniaPlanResultsProducts,
  };

  window.PlenniaPlanQuizExports = {
    getQuizState,
    normalizeQuizState,
    mapAgeGroup,
    recommendProducts,
    buildPetProfileFromQuiz,
    hydrateCustomerPets,
    syncCustomerPets,
    buildCustomerPetsEnvelope,
  };
})();